import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { formatNumber } from '../components/shared/UI';
import {
  Target, Flag, TrendingUp, Vote, Save, Plus, Trash2, Edit2, Check, Info,
  MapPin as MapPinIcon, Trophy, Layers, Map as MapIcon, BarChart2, CheckCircle2, ChevronRight,
  ArrowRight, Sparkles, AlertCircle, ShieldAlert, Users, Award, ExternalLink, Calculator,
  Download, Upload, FileSpreadsheet, Filter
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { wilayahTargetApi, statsApi, quickCountApi, dapilApi, desaApi } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { KECAMATAN_LIST as ALL_KEC } from '../mock/mockData';
import EntityFormDialog from '../components/EntityFormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { AuthContext } from '../context/AuthContext';
import { MASTER_DAPIL_DATA, TOTAL_KABUPATEN } from '../lib/dapilMasterData';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import PetaDapilLeaflet from '../components/shared/PetaDapilLeaflet';
import SainteLagueSimulator from '../components/suara/SainteLagueSimulator';

const DAPIL_COLORS = {
  'Dapil 1': { bg: '#EFF6FF', text: '#1D4ED8', border: '#93C5FD', fill: '#3B82F6', map: '#93C5FD' },
  'Dapil 2': { bg: '#FFF7ED', text: '#C2410C', border: '#FDBA74', fill: '#F97316', map: '#FDBA74' },
  'Dapil 3': { bg: '#FEFCE8', text: '#A16207', border: '#FDE047', fill: '#EAB308', map: '#FDE047' },
  'Dapil 4': { bg: '#ECFDF5', text: '#047857', border: '#6EE7B7', fill: '#10B981', map: '#6EE7B7' },
  'Dapil 5': { bg: '#FAF5FF', text: '#7E22CE', border: '#D8B4FE', fill: '#A855F7', map: '#D8B4FE' },
  'Dapil 6': { bg: '#F0FDFA', text: '#0F766E', border: '#5EEAD4', fill: '#14B8A6', map: '#5EEAD4' },
};

const normalizeRwName = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  const m = s.match(/^(?:rw\s*)?(\d+)$/i);
  if (m) {
    const num = parseInt(m[1], 10);
    return `RW ${String(num).padStart(3, '0')}`;
  }
  return s;
};

const normDesa = (s) => String(s || '').toLowerCase().replace(/[\s_\-]+/g, '');

// ============ Target Suara 2029 Master Command Module ============
export const TargetSuara = () => {
  const { user } = React.useContext(AuthContext) || {};
  // Sesuai aturan keamanan: Hanya Super Admin yang berhak mengubah target atau mengunggah data target
  const isAuthorized = user?.role === 'super_admin';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab State: basis_data | distribusi | peta_target | simulasi
  const currentTab = searchParams.get('tab') || 'basis_data';
  const [activeTab, setActiveTab] = useState(
    ['basis_data', 'distribusi', 'peta_target', 'simulasi'].includes(currentTab) ? currentTab : (currentTab === 'rincian' ? 'distribusi' : 'basis_data')
  );

  // Filter Kategori Dapil (semua | Potensial | Menantang | Perjuangan)
  const [kategoriFilter, setKategoriFilter] = useState('semua');

  // Selected Dapil for Tab Distribusi & Peta
  const [selectedDapilName, setSelectedDapilName] = useState(searchParams.get('dapil') || 'Dapil 1');

  // Hierarchical Level untuk Tab Distribusi Target per Dapil:
  // level: 'dapil' | 'kecamatan' | 'desa'
  const [distLevel, setDistLevel] = useState({
    level: searchParams.get('kecamatan') ? 'kecamatan' : 'dapil',
    dapil: searchParams.get('dapil') || 'Dapil 1',
    kecamatan: searchParams.get('kecamatan') || '',
    desa: searchParams.get('desa') || ''
  });

  // Data State
  const [loading, setLoading] = useState(true);
  const [kabData, setKabData] = useState(null);
  const [dapilList, setDapilList] = useState([]);
  const [dapilRealisasi, setDapilRealisasi] = useState({});
  const [targetItems, setTargetItems] = useState([]);
  const [desaList, setDesaList] = useState([]);
  const [statsKecamatan, setStatsKecamatan] = useState([]);
  const [statsDesa, setStatsDesa] = useState([]);
  const [rwRealisasiList, setRwRealisasiList] = useState([]);
  const [rwLoading, setRwLoading] = useState(false);

  // Upload & Template State (Super Admin Only)
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  // Modal Edit Target & Baseline Manual
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTargetItem, setEditTargetItem] = useState(null);
  const [editForm, setEditForm] = useState({
    level: 'kecamatan',
    wilayah_id: '',
    dapil: '',
    kecamatan: '',
    desa: '',
    rw: '',
    baseline_suara_2024: '',
    target_suara_2029: '',
    kategori: '',
    catatan: '',
    isNew: false,
  });
  const [initialEditForm, setInitialEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sync state with URL params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      if (['basis_data', 'distribusi', 'peta_target', 'simulasi'].includes(tabFromUrl)) {
        setActiveTab(tabFromUrl);
      } else if (tabFromUrl === 'rincian') {
        setActiveTab('distribusi');
      }
    }
    const dapilFromUrl = searchParams.get('dapil');
    if (dapilFromUrl) {
      setSelectedDapilName(dapilFromUrl);
      setDistLevel(prev => ({ ...prev, dapil: dapilFromUrl }));
    }
    const kecFromUrl = searchParams.get('kecamatan');
    if (kecFromUrl) {
      setDistLevel(prev => ({ ...prev, level: 'kecamatan', kecamatan: kecFromUrl }));
    }
  }, [searchParams]);

  const switchTab = (tab, dapil = null) => {
    const targetTab = tab === 'rincian' ? 'distribusi' : tab;
    setActiveTab(targetTab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', targetTab);
    if (dapil) {
      newParams.set('dapil', dapil);
      setSelectedDapilName(dapil);
      setDistLevel({ level: 'dapil', dapil, kecamatan: '', desa: '' });
      newParams.delete('kecamatan');
      newParams.delete('desa');
    }
    setSearchParams(newParams);
  };

  // Muat Data Utama
  const loadMainData = async () => {
    setLoading(true);
    try {
      const [kabRes, dapilsRes, targetsRes, desaRes, statsKecRes, statsDesaRes] = await Promise.all([
        wilayahTargetApi.realisasi({ level: 'kabupaten' }).catch(() => ({ data: null })),
        dapilApi.list().catch(() => ({ data: [] })),
        wilayahTargetApi.list().catch(() => ({ data: [] })),
        desaApi.list().catch(() => ({ data: [] })),
        statsApi.kecamatanDetail().catch(() => ({ data: [] })),
        statsApi.desaDetail().catch(() => ({ data: [] })),
      ]);

      setKabData(kabRes?.data || null);
      setDapilList(dapilsRes?.data || []);
      setTargetItems(targetsRes?.data || []);
      setDesaList(desaRes?.data || []);
      setStatsKecamatan(statsKecRes?.data || []);
      setStatsDesa(statsDesaRes?.data || []);

      if (dapilsRes?.data && dapilsRes.data.length > 0) {
        const dapilPromises = dapilsRes.data.map(d =>
          wilayahTargetApi.realisasi({ level: 'dapil', dapil: d.nama })
            .then(res => ({ dapil: d.nama, data: res.data }))
            .catch(() => ({ dapil: d.nama, data: null }))
        );
        const dapilResults = await Promise.all(dapilPromises);
        const mapDapil = {};
        dapilResults.forEach(r => { mapDapil[r.dapil] = r.data; });
        setDapilRealisasi(mapDapil);
      }
    } catch (err) {
      console.error("Gagal memuat data target suara:", err);
      toast({ title: "Gagal memuat data", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMainData();
  }, []);

  // Muat RW saat drilldown ke level Desa
  useEffect(() => {
    const curKec = distLevel.kecamatan;
    const curDesa = distLevel.desa;
    if (distLevel.level === 'desa' && curKec && curDesa) {
      setRwLoading(true);
      statsApi.rwDetail().then(res => {
        const filtered = (res?.data || []).filter(
          r => r.kecamatan?.toLowerCase() === curKec?.toLowerCase() &&
               normDesa(r.desa) === normDesa(curDesa)
        );
        setRwRealisasiList(filtered);
      }).catch((err) => {
        console.error("Gagal memuat detail RW:", err);
        setRwRealisasiList([]);
      }).finally(() => {
        setRwLoading(false);
      });
    }
  }, [distLevel]);

  // Modal Edit Handler
  const openEditModal = (item) => {
    if (!isAuthorized) {
      toast({ title: "Akses Ditolak", description: "Hanya Super Admin yang berhak mengubah target.", variant: "destructive" });
      return;
    }
    setEditTargetItem(item);
    const formVals = {
      level: item.level || 'kecamatan',
      wilayah_id: item.wilayah_id || '',
      dapil: item.dapil || '',
      kecamatan: item.kecamatan || '',
      desa: item.desa || '',
      rw: item.rw || '',
      baseline_suara_2024: item.baseline_suara_2024 !== null && item.baseline_suara_2024 !== undefined ? String(item.baseline_suara_2024) : '',
      target_suara_2029: item.target_suara_2029 !== null && item.target_suara_2029 !== undefined ? String(item.target_suara_2029) : '',
      kategori: item.kategori || '',
      catatan: item.catatan || '',
      isNew: Boolean(item.isNew),
    };
    setEditForm(formVals);
    setInitialEditForm(formVals);
    setEditModalOpen(true);
  };

  const isFormDirty = useMemo(() => {
    if (!initialEditForm) return false;
    return Object.keys(initialEditForm).some(key => initialEditForm[key] !== editForm[key]);
  }, [initialEditForm, editForm]);

  const requestCloseModal = () => {
    if (isFormDirty) {
      if (window.confirm("Perubahan belum disimpan. Yakin ingin membatalkan?")) {
        setEditModalOpen(false);
        setInitialEditForm(null);
      }
    } else {
      setEditModalOpen(false);
      setInitialEditForm(null);
    }
  };

  const validateNumericField = (val, fieldLabel) => {
    if (val === '' || val === null || val === undefined) return null;
    const trimmed = String(val).trim();
    if (trimmed === '') return null;
    if (!/^\d+$/.test(trimmed)) {
      throw new Error(`${fieldLabel} harus berupa bilangan bulat positif atau nol tanpa desimal.`);
    }
    const num = Number(trimmed);
    if (!Number.isSafeInteger(num) || num < 0) {
      throw new Error(`${fieldLabel} tidak valid atau bernilai negatif.`);
    }
    return num;
  };

  const handleSaveEdit = async () => {
    let parsedBaseline = null;
    let parsedTarget = null;
    try {
      parsedBaseline = validateNumericField(editForm.baseline_suara_2024, "Baseline Suara 2024");
      parsedTarget = validateNumericField(editForm.target_suara_2029, "Target Suara 2029");
    } catch (valErr) {
      toast({ title: "Input Tidak Valid", description: valErr.message, variant: "destructive" });
      return;
    }

    if (editForm.level === 'desa') {
      if (parsedTarget !== null && parsedTarget <= 0) {
        toast({ title: "Target Tidak Valid", description: "Target Suara Desa harus berupa angka positif (> 0).", variant: "destructive" });
        return;
      }
    }

    if (editForm.level === 'rw') {
      const rwTrimmed = editForm.rw.trim();
      if (!rwTrimmed) {
        toast({ title: "Input Tidak Lengkap", description: "Nomor/Nama RW wajib diisi untuk target level RW.", variant: "destructive" });
        return;
      }
      if (parsedTarget !== null && parsedTarget < 0) {
        toast({ title: "Target Tidak Valid", description: "Target Suara RW harus berupa angka non-negatif (>= 0).", variant: "destructive" });
        return;
      }

      const canonicalRw = normalizeRwName(rwTrimmed);

      // Cegah duplikasi jika mode tambah
      if (editForm.isNew) {
        const existing = targetItems.find(
          it => it.level === 'rw' &&
                it.kecamatan?.toLowerCase() === editForm.kecamatan?.toLowerCase() &&
                it.desa?.toLowerCase() === editForm.desa?.toLowerCase() &&
                normalizeRwName(it.rw).toLowerCase() === canonicalRw.toLowerCase() &&
                it.target_suara_2029 !== null && it.target_suara_2029 !== undefined
        );
        if (existing) {
          toast({
            title: "Target RW Sudah Ada",
            description: `Target untuk ${canonicalRw} sudah ditetapkan sebesar ${formatNumber(existing.target_suara_2029)} suara. Silakan gunakan tombol Edit Target pada tabel RW untuk mengubahnya.`,
            variant: "destructive"
          });
          return;
        }
      }
    }

    setSaving(true);
    try {
      const payload = {
        level: editForm.level,
        wilayah_id: editForm.wilayah_id || undefined,
        dapil: editForm.dapil || undefined,
        kecamatan: editForm.kecamatan || undefined,
        desa: editForm.desa || undefined,
        rw: editForm.rw ? normalizeRwName(editForm.rw) : undefined,
        baseline_suara_2024: parsedBaseline,
        target_suara_2029: parsedTarget,
        kategori: editForm.kategori ? editForm.kategori.trim() : undefined,
        catatan: editForm.catatan ? editForm.catatan.trim() : "Pembaruan manual melalui modul target",
      };

      await wilayahTargetApi.upsert(payload);
      toast({ title: "Berhasil disimpan", description: `Target untuk ${editForm.wilayah_id || editForm.rw || editForm.desa || editForm.kecamatan || editForm.dapil || 'Kabupaten'} berhasil diperbarui.` });
      setEditModalOpen(false);
      setInitialEditForm(null);
      loadMainData();
    } catch (err) {
      toast({ title: "Gagal menyimpan", description: err.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Download Template Excel Target Suara Wilayah (Super Admin Only)
  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const res = await wilayahTargetApi.templateExcel();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_target_suara_sukabumi.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: "Template Berhasil Diunduh", description: "Gunakan template ini untuk mengisi atau mengupdate target suara dan baseline per wilayah." });
    } catch (err) {
      console.error("Gagal unduh template target:", err);
      toast({ title: "Gagal Mengunduh Template", description: err.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Upload / Import File Excel Target Suara dengan Mekanisme Replace (UPSERT)
  const handleUploadTarget = async (e) => {
    if (e) e.preventDefault();
    if (!uploadFile) {
      toast({ title: "Pilih File", description: "Pilih file Excel (.xlsx/.xls) atau .csv terlebih dahulu.", variant: "destructive" });
      return;
    }
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      setUploading(true);
      const res = await wilayahTargetApi.importExcel(formData);
      toast({
        title: "Impor Target Berhasil",
        description: res.data?.message || `${res.data?.processed || 0} target wilayah berhasil diperbarui.`
      });
      setUploadModalOpen(false);
      setUploadFile(null);
      loadMainData();
    } catch (err) {
      console.error("Gagal impor target:", err);
      toast({
        title: "Gagal Impor Target",
        description: err.response?.data?.detail || err.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  // Helper Gap
  const renderGap = (target, baseline) => {
    if (target === null || target === undefined || baseline === null || baseline === undefined) {
      return <span className="text-gray-400 font-medium">-</span>;
    }
    const gap = target - baseline;
    const isPositive = gap >= 0;
    return (
      <span className={`font-black ${isPositive ? 'text-amber-600' : 'text-rose-600'}`}>
        {isPositive ? `+${formatNumber(gap)}` : formatNumber(gap)}
      </span>
    );
  };

  // Filter Kategori Dapil
  const filteredKategoriDapil = useMemo(() => {
    if (kategoriFilter === 'semua') return MASTER_DAPIL_DATA;
    return MASTER_DAPIL_DATA.filter(d => d.kategori?.toLowerCase() === kategoriFilter.toLowerCase());
  }, [kategoriFilter]);

  // Selected Dapil Data from MASTER
  const activeDapilMaster = useMemo(() => {
    return MASTER_DAPIL_DATA.find(d => d.dapil === selectedDapilName) || MASTER_DAPIL_DATA[0];
  }, [selectedDapilName]);

  // Realisasi Dapil Aktif dari Backend
  const activeDapilReal = dapilRealisasi[selectedDapilName] || null;

  // DPT Total Dapil Aktif (dinamis dari data DPT riil jika ada, atau master PPT)
  const activeDapilDpt = useMemo(() => {
    const dapilKecNames = (activeDapilMaster.kecamatan || []).map(k => k.nama?.toLowerCase());
    const sumFromStats = statsKecamatan
      .filter(k => dapilKecNames.includes(k.kecamatan?.toLowerCase()))
      .reduce((acc, k) => acc + (k.dpt_count || 0), 0);
    return sumFromStats > 0 ? sumFromStats : activeDapilMaster.dpt_2024;
  }, [activeDapilMaster, statsKecamatan]);

  // Menghitung Target Desa & Target RW yang secara eksplisit ditetapkan sebagai cakupan target
  const getTargetCakupan = useCallback((dapilName) => {
    const dapilMaster = MASTER_DAPIL_DATA.find(d => d.dapil === dapilName);
    const normK = (s) => String(s || '').toLowerCase().replace(/[\s_\-]+/g, '');
    const dapilKecs = dapilMaster?.kecamatan.map(k => normK(k.nama)) || [];

    // Kumpulkan seluruh Desa unik di Dapil ini yang memiliki target suara desa atau target suara RW
    const targetedDesasInDapil = new Set();
    const targetedRwsInDapil = new Set();
    let totalTargetSuaraRw = 0;

    targetItems.forEach(it => {
      const itKec = normK(it.kecamatan);
      const isTargeted = it.target_suara_2029 !== null && it.target_suara_2029 !== undefined && Number(it.target_suara_2029) > 0;
      if (!isTargeted) return;

      // Verifikasi presisi: kecamatan wajib benar-benar berada di Dapil ini
      if (dapilKecs.includes(itKec)) {
        if (it.level === 'desa' && it.desa) {
          targetedDesasInDapil.add(`${itKec}|${normDesa(it.desa)}`);
        } else if (it.level === 'rw' && it.desa) {
          targetedDesasInDapil.add(`${itKec}|${normDesa(it.desa)}`);
          if (it.rw) {
            targetedRwsInDapil.add(`${itKec}|${normDesa(it.desa)}|${normalizeRwName(it.rw)}`);
            totalTargetSuaraRw += Number(it.target_suara_2029) || 0;
          }
        }
      }
    });

    const dapilSetting = targetItems.find(it =>
      it.level === 'dapil' &&
      (it.dapil === dapilName || it.wilayah_id === `dapil:${dapilName.toLowerCase().replace(/[\s/]+/g, '-')}`)
    );

    // Target Desa tidak boleh melebihi total desa/kelurahan definitif di Dapil tersebut
    const maxDesaInDapil = dapilMaster?.desa_count || 1000;
    const computedDesa = targetedDesasInDapil.size > 0 ? Math.min(targetedDesasInDapil.size, maxDesaInDapil) : null;
    const targetDesaCount = dapilSetting?.target_desa_cakupan !== undefined && dapilSetting?.target_desa_cakupan !== null
      ? Math.min(Number(dapilSetting.target_desa_cakupan), maxDesaInDapil)
      : computedDesa;

    // Target RW tidak boleh melebihi total RW definitif di Dapil tersebut
    const maxRwInDapil = dapilMaster?.rw_count || 5000;
    const computedRw = targetedRwsInDapil.size > 0 ? Math.min(targetedRwsInDapil.size, maxRwInDapil) : null;
    const targetRwCount = dapilSetting?.target_rw_cakupan !== undefined && dapilSetting?.target_rw_cakupan !== null
      ? Math.min(Number(dapilSetting.target_rw_cakupan), maxRwInDapil)
      : computedRw;

    // Komparasi Strategis Top-Down (Target Kursi) vs Bottom-Up (Agregat Target RW)
    const targetStrategis = dapilMaster?.target_suara_2029 || 0;
    const selisihBuffer = totalTargetSuaraRw - targetStrategis;
    const bufferPct = targetStrategis > 0 ? ((totalTargetSuaraRw - targetStrategis) / targetStrategis) * 100 : 0;
    const isSurplus = totalTargetSuaraRw >= targetStrategis;

    return {
      targetDesa: targetDesaCount,
      targetRw: targetRwCount,
      totalTargetSuaraRw,
      targetStrategis,
      selisihBuffer,
      bufferPct,
      isSurplus,
    };
  }, [targetItems]);

  // Total Cakupan Target Kabupaten
  const totalCakupanKab = useMemo(() => {
    let sumDesa = 0;
    let sumRw = 0;
    let sumTargetRw = 0;
    let hasDesa = false;
    let hasRw = false;

    MASTER_DAPIL_DATA.forEach(d => {
      const c = getTargetCakupan(d.dapil);
      if (c.targetDesa !== null) { sumDesa += c.targetDesa; hasDesa = true; }
      if (c.targetRw !== null) { sumRw += c.targetRw; hasRw = true; }
      sumTargetRw += c.totalTargetSuaraRw || 0;
    });

    const targetStrategisKab = TOTAL_KABUPATEN.target_suara_2029 || 256204;
    const selisihBufferKab = sumTargetRw - targetStrategisKab;
    const bufferPctKab = targetStrategisKab > 0 ? ((sumTargetRw - targetStrategisKab) / targetStrategisKab) * 100 : 0;

    return {
      totalDesa: hasDesa ? sumDesa : null,
      totalRw: hasRw ? sumRw : null,
      totalTargetSuaraRw: sumTargetRw,
      targetStrategisKab,
      selisihBufferKab,
      bufferPctKab,
      isSurplus: sumTargetRw >= targetStrategisKab,
    };
  }, [getTargetCakupan]);

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* 1. HEADER BANNER UTAMA */}
      <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-orange-500 rounded-2xl p-4 sm:p-5 text-white card-shadow relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-10 flex items-center justify-end pr-8 pointer-events-none">
          <Target className="w-52 h-52 text-white" />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-[10px] font-bold text-white mb-1.5">
              <Sparkles className="w-3 h-3" /> STRATEGI PEMENANGAN PEMILU 2029
            </div>
            <h1 className="text-xl sm:text-2xl font-black tracking-tight">
              Target Suara 2029 — Kabupaten Sukabumi
            </h1>
            <p className="text-orange-100 text-xs mt-0.5 max-w-2xl font-medium">
              Perencanaan sasaran perolehan suara resmi PKS Kabupaten Sukabumi berbasis DPT, target 9 kursi DPRD, ambang batas kompetisi, dan distribusi hierarkis sampai ke tingkat RW.
            </p>
          </div>
          <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-md p-2.5 rounded-xl border border-white/20 self-start md:self-center">
            <div className="text-right">
              <p className="text-[10px] text-orange-200 uppercase font-bold tracking-wider">Target Kursi Utama</p>
              <p className="text-xl sm:text-2xl font-black text-white">9 Kursi</p>
            </div>
            <div className="h-8 w-px bg-white/20"></div>
            <div className="text-right">
              <p className="text-[10px] text-orange-200 uppercase font-bold tracking-wider">Total Target Suara</p>
              <p className="text-xl sm:text-2xl font-black text-amber-200">256.204</p>
            </div>
          </div>
        </div>

        {/* Metrik Kabupaten Ringkas */}
        <div className="mt-3.5 pt-3 border-t border-white/20 grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
          <div className="bg-black/10 rounded-lg p-2 sm:p-2.5 backdrop-blur-sm">
            <span className="text-orange-200 block text-[10px] sm:text-[11px]">Total DPT 2024</span>
            <span className="font-extrabold text-xs sm:text-sm text-white">1.997.822 Pemilih</span>
          </div>
          <div className="bg-black/10 rounded-lg p-2 sm:p-2.5 backdrop-blur-sm">
            <span className="text-orange-200 block text-[10px] sm:text-[11px]">Baseline 2024</span>
            <span className="font-extrabold text-xs sm:text-sm text-white">161.304 Suara (7 Kursi)</span>
          </div>
          <div className="bg-black/10 rounded-lg p-2 sm:p-2.5 backdrop-blur-sm">
            <span className="text-orange-200 block text-[10px] sm:text-[11px]">Kebutuhan Gap Suara</span>
            <span className="font-extrabold text-xs sm:text-sm text-amber-200">+94.900 Suara</span>
          </div>
          <div className="bg-black/10 rounded-lg p-2 sm:p-2.5 backdrop-blur-sm">
            <span className="text-orange-200 block text-[10px] sm:text-[11px]">Cakupan Struktur Master</span>
            <span className="font-extrabold text-xs sm:text-sm text-white">47 Kec · 386 Desa · 3.228 RW</span>
          </div>
        </div>
      </div>

      {/* 2. SUB-NAVIGASI TAB */}
      <div className="bg-white rounded-xl p-1.5 card-shadow border border-gray-100 flex flex-wrap gap-1">
        <button
          onClick={() => switchTab('basis_data')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'basis_data'
              ? 'bg-orange-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          <span>1. Peta Dapil & Basis Data</span>
        </button>

        <button
          onClick={() => switchTab('distribusi')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'distribusi'
              ? 'bg-orange-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
          }`}
        >
          <BarChart2 className="w-3.5 h-3.5" />
          <span>2. Distribusi Target per Dapil</span>
        </button>

        <button
          onClick={() => switchTab('peta_target')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'peta_target'
              ? 'bg-orange-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
          }`}
        >
          <MapIcon className="w-3.5 h-3.5" />
          <span>3. Distribusi Target di Peta Wilayah</span>
        </button>

        <button
          onClick={() => switchTab('simulasi')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'simulasi'
              ? 'bg-orange-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
          }`}
        >
          <Calculator className="w-3.5 h-3.5" />
          <span>4. Simulasi Sainte-Laguë 2029</span>
          <span className="text-[9px] px-1.5 py-0.2 bg-amber-400 text-gray-950 rounded-full font-black ml-1">
            Baru
          </span>
        </button>
      </div>

      {/* ============================================================== */}
      {/* VIEW 1: PETA DAPIL & BASIS DATA (RINGKASAN MASTER & CAKUPAN WILAYAH) */}
      {/* ============================================================== */}
      {activeTab === 'basis_data' && (
        <div className="space-y-4 sm:space-y-5">
          {/* 6 KARTU RINGKASAN CAKUPAN DAPIL DI ATAS */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
            {MASTER_DAPIL_DATA.map((d) => {
              const isSelected = selectedDapilName === d.dapil;
              const cakupan = getTargetCakupan(d.dapil);
              return (
                <div
                  key={d.dapil}
                  onClick={() => {
                    switchTab('distribusi', d.dapil);
                    setDistLevel({ level: 'dapil', dapil: d.dapil, kecamatan: '', desa: '' });
                  }}
                  className={`p-3 rounded-xl border transition-all cursor-pointer card-hover ${
                    isSelected
                      ? 'ring-2 ring-orange-500 bg-white shadow-md'
                      : 'bg-white hover:border-orange-300 shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-extrabold text-xs text-gray-900">{d.nama}</span>
                    <Badge
                      className={`text-[10px] px-1.5 py-0 font-extrabold ${
                        d.kategori === 'Potensial'
                          ? 'bg-emerald-100 text-emerald-800'
                          : d.kategori === 'Menantang'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {d.target_kursi_2029} Kursi
                    </Badge>
                  </div>
                  <p className="text-[10px] text-gray-500 font-semibold">{d.kecamatan_count} Kec · {d.desa_count} Desa · {formatNumber(d.rw_count)} RW</p>
                  <div className="mt-2 pt-2 border-t border-gray-100 text-xs">
                    <div className="flex justify-between items-baseline text-[11px] text-gray-600">
                      <span>Baseline:</span>
                      <span className="font-bold text-gray-900">{formatNumber(d.baseline_2024)}</span>
                    </div>
                    <div className="flex justify-between items-baseline text-[11px] text-gray-600 mt-0.5">
                      <span>DPT 2024:</span>
                      <span className="font-medium text-gray-700">{formatNumber(d.dpt_2024)}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] font-bold text-orange-600">
                    <span>Lihat Distribusi</span>
                    <ChevronRight className="w-3 h-3" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* GRID: PETA 6 DAPIL (KIRI) & TABEL MASTER DAPIL (KANAN) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
            {/* SISI KIRI: PETA SKEMATIK 6 DAPIL & AMBANG KURSI */}
            <div className="lg:col-span-5 bg-white rounded-xl p-4 sm:p-5 card-shadow border border-gray-100 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2">
                    <MapIcon className="w-4.5 h-4.5 text-orange-600" />
                    <h3 className="font-extrabold text-sm sm:text-base text-gray-900">Peta 6 Dapil Kab. Sukabumi</h3>
                  </div>
                  <Badge variant="outline" className="text-xs font-bold text-orange-700 bg-orange-50 border-orange-200">
                    Target 9 Kursi
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 font-medium mb-3">
                  Cakupan 6 Daerah Pemilihan dengan target kursi dan batas ambang kompetisi Pemilu 2029.
                </p>

                {/* Peta Interaktif OpenStreetMap 6 Dapil Kab. Sukabumi */}
                <div className="mt-2">
                  <PetaDapilLeaflet
                    height={270}
                    showControls={true}
                    showLegend={true}
                    onSelectDapil={(dapil) => {
                      switchTab('distribusi', dapil);
                      setDistLevel({ level: 'dapil', dapil, kecamatan: '', desa: '' });
                    }}
                  />
                </div>
              </div>

              {/* PANEL AMBANG KURSI TAMBAHAN */}
              <div className="mt-3.5 p-3.5 rounded-xl bg-amber-50/70 border border-amber-200">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Award className="w-4 h-4 text-amber-700" />
                  <p className="font-extrabold text-xs text-amber-900 uppercase">Ambang Batas Kursi Tambahan</p>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-amber-200">
                    <div>
                      <span className="font-bold text-gray-900">Dapil 3: Ambang Kursi ke-2</span>
                      <p className="text-[10px] text-gray-500">Target 2 Kursi (Kategori: Menantang)</p>
                    </div>
                    <span className="font-black text-amber-700">&gt; 50.938 Suara</span>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-amber-200">
                    <div>
                      <span className="font-bold text-gray-900">Dapil 4: Ambang Kursi ke-3</span>
                      <p className="text-[10px] text-gray-500">Target 3 Kursi (Kategori: Potensial)</p>
                    </div>
                    <span className="font-black text-emerald-700">&gt; 78.106 Suara</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SISI KANAN: TABEL MASTER RINGKASAN & CAKUPAN WILAYAH 6 DAPIL */}
            <div className="lg:col-span-7 bg-white rounded-xl p-4 sm:p-5 card-shadow border border-gray-100 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <div>
                    <h3 className="font-extrabold text-sm sm:text-base text-gray-900">Tabel Master & Cakupan Wilayah Dapil</h3>
                    <p className="text-xs text-gray-500 font-medium">Ringkasan master wilayah dan penetapan cakupan target per Dapil</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => switchTab('distribusi')}
                    className="gap-1 text-xs font-bold border-orange-200 text-orange-700 hover:bg-orange-50 h-8"
                  >
                    Buka Distribusi Target <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-orange-50/50">
                        <TableHead className="font-extrabold">Dapil</TableHead>
                        <TableHead className="font-extrabold text-center">Desa</TableHead>
                        <TableHead className="font-extrabold text-center">RW</TableHead>
                        <TableHead className="font-extrabold text-right">DPT 2024</TableHead>
                        <TableHead className="font-extrabold text-right">Baseline 2024</TableHead>
                        <TableHead className="font-extrabold text-right bg-orange-100/40">
                          <div className="flex flex-col items-end">
                            <span>Target Kursi 2029</span>
                            <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Top-Down (Ambang)</span>
                          </div>
                        </TableHead>
                        <TableHead className="font-extrabold text-right bg-emerald-50/50">
                          <div className="flex flex-col items-end">
                            <span>Agregat Target RW</span>
                            <span className="text-[9px] font-semibold text-emerald-700 uppercase tracking-tight">Bottom-Up (Riil)</span>
                          </div>
                        </TableHead>
                        <TableHead className="font-extrabold text-center">
                          <div className="flex flex-col items-center">
                            <span>Kesiapan / Buffer</span>
                            <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Safety Margin</span>
                          </div>
                        </TableHead>
                        <TableHead className="font-extrabold text-center">Cakupan Desa</TableHead>
                        <TableHead className="font-extrabold text-center">Cakupan RW</TableHead>
                        <TableHead className="font-extrabold text-center">Kategori</TableHead>
                        <TableHead className="font-extrabold text-center">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {MASTER_DAPIL_DATA.map((d) => {
                        const cakupan = getTargetCakupan(d.dapil);
                        const kategoriBadgeClass =
                          d.kategori === 'Potensial'
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                            : d.kategori === 'Menantang'
                            ? 'bg-amber-100 text-amber-800 border-amber-300'
                            : 'bg-rose-100 text-rose-800 border-rose-300';
                        return (
                          <TableRow key={d.dapil} className="hover:bg-orange-50/30 text-xs">
                            <TableCell className="font-extrabold text-gray-900">{d.nama}</TableCell>
                            <TableCell className="text-center font-semibold text-gray-700">{d.desa_count}</TableCell>
                            <TableCell className="text-center font-semibold text-gray-700">{formatNumber(d.rw_count)}</TableCell>
                            <TableCell className="text-right font-medium text-gray-700">{formatNumber(d.dpt_2024)}</TableCell>
                            <TableCell className="text-right font-medium text-gray-700">{formatNumber(d.baseline_2024)}</TableCell>
                            <TableCell className="text-right bg-orange-50/20">
                              <div className="font-black text-orange-600 text-xs sm:text-sm">
                                {formatNumber(d.target_suara_2029)}
                              </div>
                              <Badge className="bg-orange-100 text-orange-800 font-extrabold text-[10px] mt-0.5">
                                {d.target_kursi_2029} Kursi
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right bg-emerald-50/20 font-black text-emerald-800 text-xs sm:text-sm">
                              {cakupan.totalTargetSuaraRw > 0 ? (
                                <div>
                                  <div>{formatNumber(cakupan.totalTargetSuaraRw)}</div>
                                  <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">{cakupan.targetRw || 0} RW</p>
                                </div>
                              ) : (
                                <span className="text-gray-400 font-semibold italic">Belum diisi</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {cakupan.totalTargetSuaraRw > 0 ? (
                                cakupan.isSurplus ? (
                                  <div className="flex flex-col items-center">
                                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 font-black text-[10px] px-1.5 py-0.5 gap-0.5">
                                      <TrendingUp className="w-2.5 h-2.5 text-emerald-700" />
                                      +{formatNumber(cakupan.selisihBuffer)} (+{cakupan.bufferPct.toFixed(1)}%)
                                    </Badge>
                                    <span className="text-[9px] text-emerald-700 font-bold mt-0.5">Surplus Aman</span>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center">
                                    <Badge className="bg-amber-100 text-amber-800 border border-amber-300 font-black text-[10px] px-1.5 py-0.5">
                                      {formatNumber(cakupan.selisihBuffer)} ({cakupan.bufferPct.toFixed(1)}%)
                                    </Badge>
                                    <span className="text-[9px] text-amber-700 font-bold mt-0.5">Defisit Target</span>
                                  </div>
                                )
                              ) : (
                                <span className="text-gray-400 text-[11px] font-semibold italic">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {cakupan.targetDesa !== null ? (
                                <span className="font-extrabold text-orange-700">{cakupan.targetDesa} Desa</span>
                              ) : (
                                <span className="text-gray-400 font-semibold italic">Belum ditetapkan</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {cakupan.targetRw !== null ? (
                                <span className="font-extrabold text-orange-700">{cakupan.targetRw} RW</span>
                              ) : (
                                <span className="text-gray-400 font-semibold italic">Belum ditetapkan</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={`font-black text-[10px] ${kategoriBadgeClass}`}>
                                {d.kategori}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                onClick={() => {
                                  switchTab('distribusi', d.dapil);
                                  setDistLevel({ level: 'dapil', dapil: d.dapil, kecamatan: '', desa: '' });
                                }}
                                className="text-xs h-7 px-2.5 font-bold bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
                              >
                                Lihat Distribusi
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {/* Baris Total Kabupaten */}
                      <TableRow className="bg-amber-50/70 font-black text-xs border-t-2 border-orange-300">
                        <TableCell className="font-black text-gray-900">Total Kab.</TableCell>
                        <TableCell className="text-center font-black text-gray-900">{TOTAL_KABUPATEN.desa_count}</TableCell>
                        <TableCell className="text-center font-black text-gray-900">{formatNumber(TOTAL_KABUPATEN.rw_count)}</TableCell>
                        <TableCell className="text-right font-black text-gray-900">{formatNumber(TOTAL_KABUPATEN.dpt_2024)}</TableCell>
                        <TableCell className="text-right font-black text-gray-900">{formatNumber(TOTAL_KABUPATEN.baseline_2024)}</TableCell>
                        <TableCell className="text-right bg-orange-100/30 font-black text-gray-900">
                          <div className="font-black text-orange-700 text-sm">
                            {formatNumber(TOTAL_KABUPATEN.target_suara_2029)}
                          </div>
                          <Badge className="bg-orange-600 text-white font-black text-[10px] mt-0.5">
                            {TOTAL_KABUPATEN.target_kursi_2029} Kursi
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right bg-emerald-100/30 font-black text-emerald-900 text-xs sm:text-sm">
                          <div>{formatNumber(totalCakupanKab.totalTargetSuaraRw)}</div>
                          <p className="text-[10px] text-emerald-700 font-bold mt-0.5">{totalCakupanKab.totalRw || 0} RW</p>
                        </TableCell>
                        <TableCell className="text-center">
                          {totalCakupanKab.totalTargetSuaraRw > 0 && (
                            <div className="flex flex-col items-center">
                              <Badge className="bg-emerald-600 text-white font-black text-[10px] px-2 py-0.5">
                                +{formatNumber(totalCakupanKab.selisihBufferKab)} (+{totalCakupanKab.bufferPctKab.toFixed(1)}%)
                              </Badge>
                              <span className="text-[9px] text-emerald-800 font-extrabold mt-0.5">Surplus Cadangan</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {totalCakupanKab.totalDesa !== null ? (
                            <span className="font-black text-orange-800">{totalCakupanKab.totalDesa} Desa</span>
                          ) : (
                            <span className="text-gray-400 font-semibold italic">Belum ditetapkan</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {totalCakupanKab.totalRw !== null ? (
                            <span className="font-black text-orange-800">{totalCakupanKab.totalRw} RW</span>
                          ) : (
                            <span className="text-gray-400 font-semibold italic">Belum ditetapkan</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-gray-800 text-white font-black text-[10px]">
                            Semua
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => switchTab('distribusi')}
                            className="text-xs h-7 px-2 font-bold text-orange-700 border-orange-300 hover:bg-orange-100"
                          >
                            Buka Semua
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                <span>Total Master: <b>47 Kecamatan · 386 Desa · 3.228 RW</b></span>
                <span className="text-[11px] text-gray-400">Distribusi perolehan suara dikelola pada menu Distribusi Target</span>
              </div>
            </div>
          </div>

          {/* TABEL KLASIFIKASI KATEGORI DAPIL & STRATEGI PEMENANGAN */}
          <div className="bg-white rounded-xl p-4 sm:p-5 card-shadow border border-gray-100 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-extrabold text-sm sm:text-base text-gray-900">
                    Tabel Kategori Dapil & Strategi Pemenangan
                  </h3>
                  <Badge className="bg-orange-100 text-orange-800 font-extrabold text-[11px]">
                    Turunan Target Suara
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  Klasifikasi wilayah pemenangan (Potensial, Menantang, Perjuangan) beserta beban target rata-rata Desa dan RW
                </p>
              </div>

              {/* Filter Pills Kategori */}
              <div className="inline-flex items-center p-1 bg-gray-100 rounded-lg text-xs font-bold gap-1">
                {[
                  { key: 'semua', label: 'Semua (6)' },
                  { key: 'Potensial', label: 'Potensial (1)' },
                  { key: 'Menantang', label: 'Menantang (1)' },
                  { key: 'Perjuangan', label: 'Perjuangan (4)' },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setKategoriFilter(f.key)}
                    className={`px-3 py-1 rounded-md transition-all text-xs ${
                      kategoriFilter === f.key
                        ? 'bg-white shadow-sm font-black text-gray-900'
                        : 'text-gray-500 hover:text-gray-900 font-semibold'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-orange-50/50">
                    <TableHead className="font-extrabold">Dapil</TableHead>
                    <TableHead className="font-extrabold text-right">Baseline 2024</TableHead>
                    <TableHead className="font-extrabold text-right">Target Suara 2029</TableHead>
                    <TableHead className="font-extrabold text-right">Kebutuhan Baru (Gap)</TableHead>
                    <TableHead className="font-extrabold text-right">Rata-rata Target Desa</TableHead>
                    <TableHead className="font-extrabold text-right">Rata-rata Target RW</TableHead>
                    <TableHead className="font-extrabold text-center">Target Kursi</TableHead>
                    <TableHead className="font-extrabold min-w-[280px]">Fokus Strategi Teritorial</TableHead>
                    <TableHead className="font-extrabold text-center">Kategori</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredKategoriDapil.map((d) => {
                    const kategoriBadgeClass =
                      d.kategori === 'Potensial'
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        : d.kategori === 'Menantang'
                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                        : 'bg-rose-100 text-rose-800 border-rose-300';
                    return (
                      <TableRow key={d.dapil} className="hover:bg-orange-50/30 text-xs">
                        <TableCell className="font-extrabold text-gray-900">{d.nama}</TableCell>
                        <TableCell className="text-right font-medium text-gray-700">{formatNumber(d.baseline_2024)}</TableCell>
                        <TableCell className="text-right font-black text-orange-600 text-sm">{formatNumber(d.target_suara_2029)}</TableCell>
                        <TableCell className="text-right font-black text-amber-600">+{formatNumber(d.gap_suara)}</TableCell>
                        <TableCell className="text-right font-bold text-gray-800">{formatNumber(Math.round(d.target_desa_avg))} suara/Desa</TableCell>
                        <TableCell className="text-right font-black text-orange-700 bg-orange-50/40">~{Math.round(d.target_rw_avg)} suara/RW</TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-orange-600 text-white font-extrabold text-[11px]">
                            {d.target_kursi_2029} Kursi
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-700 font-medium leading-relaxed">
                          {d.strategi}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className={`font-black text-[10px] ${kategoriBadgeClass}`}>
                            {d.kategori}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* VIEW 2: DISTRIBUSI TARGET PER DAPIL (PENGELOLAAN TARGET SUARA) */}
      {/* HIERARKI: KABUPATEN -> DAPIL -> KECAMATAN -> DESA/KELURAHAN -> RW */}
      {/* ============================================================== */}
      {activeTab === 'distribusi' && (
        <div className="space-y-4 sm:space-y-5">
          {/* HEADER TOOLBAR DISTRIBUSI & AKSI SUPER ADMIN */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* TAB SELECTOR 6 DAPIL */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              {MASTER_DAPIL_DATA.map((d) => (
                <button
                  key={d.dapil}
                  onClick={() => {
                    setSelectedDapilName(d.dapil);
                    setDistLevel({ level: 'dapil', dapil: d.dapil, kecamatan: '', desa: '' });
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 flex-shrink-0 ${
                    selectedDapilName === d.dapil
                      ? 'bg-orange-600 text-white shadow-sm'
                      : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'
                  }`}
                >
                  <span>{d.nama}</span>
                  <Badge className={`text-[10px] px-1.5 py-0 ${selectedDapilName === d.dapil ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-800'}`}>
                    {d.target_kursi_2029} Kursi
                  </Badge>
                </button>
              ))}
            </div>

            {/* TOMBOL AKSI SUPER ADMIN (EXPORT TEMPLATE & UPLOAD REPLCE) */}
            {isAuthorized && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                  className="text-xs font-bold border-emerald-300 text-emerald-800 hover:bg-emerald-50 h-8 gap-1.5"
                >
                  <Download className="w-3.5 h-3.5 text-emerald-600" />
                  Template Target Excel
                </Button>
                <Button
                  size="sm"
                  onClick={() => setUploadModalOpen(true)}
                  className="text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white h-8 gap-1.5 shadow-sm"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload Target Excel/CSV
                </Button>
              </div>
            )}
          </div>

          {/* BREADCRUMB & NAVIGATOR HIERARKI WILAYAH */}
          <div className="bg-white rounded-xl p-3 sm:p-3.5 card-shadow border border-gray-100 flex items-center justify-between flex-wrap gap-2.5">
            <div className="flex items-center gap-2 text-xs font-bold flex-wrap">
              <button
                onClick={() => switchTab('basis_data')}
                className="text-gray-500 hover:text-orange-600 transition-colors"
              >
                Target Suara 2029
              </button>
              <ChevronRight className="w-4 h-4 text-gray-300" />
              <button
                onClick={() => setDistLevel({ level: 'dapil', dapil: selectedDapilName, kecamatan: '', desa: '' })}
                className={`transition-colors ${distLevel.level === 'dapil' ? 'font-black text-orange-600' : 'text-gray-600 hover:text-orange-600'}`}
              >
                Distribusi Target per Dapil ({activeDapilMaster.nama})
              </button>

              {distLevel.kecamatan && (
                <>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                  <button
                    onClick={() => setDistLevel({ level: 'kecamatan', dapil: selectedDapilName, kecamatan: distLevel.kecamatan, desa: '' })}
                    className={`transition-colors ${distLevel.level === 'kecamatan' ? 'font-black text-orange-600' : 'text-gray-600 hover:text-orange-600'}`}
                  >
                    Kecamatan {distLevel.kecamatan}
                  </button>
                </>
              )}

              {distLevel.desa && (
                <>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                  <span className="font-black text-orange-600">
                    Desa/Kel. {distLevel.desa}
                  </span>
                </>
              )}
            </div>

            {distLevel.level !== 'dapil' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (distLevel.level === 'desa') {
                    setDistLevel(prev => ({ ...prev, level: 'kecamatan', desa: '' }));
                  } else if (distLevel.level === 'kecamatan') {
                    setDistLevel(prev => ({ ...prev, level: 'dapil', kecamatan: '', desa: '' }));
                  }
                }}
                className="text-xs font-bold border-gray-200 text-gray-700 hover:text-orange-600 hover:bg-orange-50 h-8 gap-1.5"
              >
                ← {distLevel.level === 'desa' ? 'Kembali ke Daftar Desa' : 'Kembali ke Daftar Kecamatan'}
              </Button>
            )}
          </div>

          {/* ========================================================== */}
          {/* TINGKAT 1: DETAIL DAPIL (8 KARTU DAPIL + TABEL KECAMATAN)  */}
          {/* ========================================================== */}
          {distLevel.level === 'dapil' && (
            <div className="space-y-4 sm:space-y-5">
              {/* 8 KARTU RINGKASAN DISTRIBUSI DAPIL (KOMPARASI TOP-DOWN VS BOTTOM-UP RW) */}
              {(() => {
                const activeDapilCakupan = getTargetCakupan(activeDapilMaster.dapil);
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5 sm:gap-3">
                    {/* 1. Jumlah Desa/Kelurahan */}
                    <div className="bg-white p-3 rounded-xl card-shadow border border-gray-100 flex flex-col justify-between">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Desa/Kelurahan</p>
                      <div className="mt-1.5">
                        <h3 className="text-lg sm:text-xl font-black text-gray-900">{activeDapilMaster.desa_count}</h3>
                        <p className="text-[10px] text-gray-400 font-semibold mt-0.5">Desa / Kelurahan</p>
                      </div>
                    </div>

                    {/* 2. Jumlah RW */}
                    <div className="bg-white p-3 rounded-xl card-shadow border border-gray-100 flex flex-col justify-between">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Jumlah RW</p>
                      <div className="mt-1.5">
                        <h3 className="text-lg sm:text-xl font-black text-gray-900">{formatNumber(activeDapilMaster.rw_count)}</h3>
                        <p className="text-[10px] text-gray-400 font-semibold mt-0.5">Rukun Warga</p>
                      </div>
                    </div>

                    {/* 3. DPT 2024 */}
                    <div className="bg-white p-3 rounded-xl card-shadow border border-gray-100 flex flex-col justify-between">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">DPT 2024</p>
                      <div className="mt-1.5">
                        <h3 className="text-lg sm:text-xl font-black text-gray-900">{formatNumber(activeDapilDpt)}</h3>
                        <p className="text-[10px] text-gray-400 font-semibold mt-0.5">Pemilih Terdaftar</p>
                      </div>
                    </div>

                    {/* 4. Baseline Suara 2024 */}
                    <div className="bg-white p-3 rounded-xl card-shadow border border-gray-100 flex flex-col justify-between">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Baseline 2024</p>
                      <div className="mt-1.5">
                        <h3 className="text-lg sm:text-xl font-black text-gray-900">{formatNumber(activeDapilMaster.baseline_2024)}</h3>
                        <p className="text-[10px] text-gray-400 font-semibold mt-0.5">Suara Sah 2024</p>
                      </div>
                    </div>

                    {/* 5. Target Kursi 2029 (Top-Down) */}
                    <div className="bg-white p-3 rounded-xl card-shadow border border-orange-200 bg-orange-50/20 flex flex-col justify-between">
                      <p className="text-[10px] font-bold text-orange-800 uppercase tracking-wider">Target Kursi (Top-Down)</p>
                      <div className="mt-1.5">
                        <h3 className="text-lg sm:text-xl font-black text-orange-600">{formatNumber(activeDapilMaster.target_suara_2029)}</h3>
                        <p className="text-[10px] text-orange-700 font-extrabold mt-0.5">{activeDapilMaster.target_kursi_2029} Kursi DPRD</p>
                      </div>
                    </div>

                    {/* 6. Agregat Target RW (Bottom-Up) */}
                    <div className="bg-white p-3 rounded-xl card-shadow border border-emerald-200 bg-emerald-50/20 flex flex-col justify-between">
                      <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Agregat Target RW</p>
                      <div className="mt-1.5">
                        <h3 className="text-lg sm:text-xl font-black text-emerald-700">
                          {activeDapilCakupan.totalTargetSuaraRw > 0 ? formatNumber(activeDapilCakupan.totalTargetSuaraRw) : '—'}
                        </h3>
                        <p className="text-[10px] text-emerald-700 font-bold mt-0.5">
                          {activeDapilCakupan.targetRw || 0} RW Bertarget
                        </p>
                      </div>
                    </div>

                    {/* 7. Kesiapan / Safety Buffer */}
                    <div className={`p-3 rounded-xl card-shadow border flex flex-col justify-between ${
                      activeDapilCakupan.isSurplus ? 'bg-emerald-50/50 border-emerald-300' : 'bg-amber-50/50 border-amber-300'
                    }`}>
                      <p className={`text-[10px] font-bold uppercase tracking-wider ${
                        activeDapilCakupan.isSurplus ? 'text-emerald-800' : 'text-amber-800'
                      }`}>
                        Kesiapan Buffer Kursi
                      </p>
                      <div className="mt-1.5">
                        {activeDapilCakupan.totalTargetSuaraRw > 0 ? (
                          <>
                            <h3 className={`text-base sm:text-lg font-black ${
                              activeDapilCakupan.isSurplus ? 'text-emerald-700' : 'text-amber-700'
                            }`}>
                              {activeDapilCakupan.isSurplus ? '+' : ''}{formatNumber(activeDapilCakupan.selisihBuffer)}
                            </h3>
                            <p className={`text-[10px] font-bold mt-0.5 ${
                              activeDapilCakupan.isSurplus ? 'text-emerald-600' : 'text-amber-600'
                            }`}>
                              {activeDapilCakupan.isSurplus ? `Surplus (+${activeDapilCakupan.bufferPct.toFixed(1)}% Aman)` : `Defisit (${activeDapilCakupan.bufferPct.toFixed(1)}%)`}
                            </p>
                          </>
                        ) : (
                          <span className="text-gray-400 font-semibold italic text-xs">Belum diisi</span>
                        )}
                      </div>
                    </div>

                    {/* 8. Kebutuhan Baru (Gap) */}
                    <div className="bg-white p-3 rounded-xl card-shadow border border-gray-100 flex flex-col justify-between">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Kebutuhan Gap</p>
                      <div className="mt-1.5">
                        <h3 className="text-lg sm:text-xl font-black text-amber-600">+{formatNumber(activeDapilMaster.gap_suara)}</h3>
                        <p className="text-[10px] text-amber-600 font-bold mt-0.5">Suara Baru</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* TABEL KECAMATAN */}
              <div className="bg-white rounded-xl p-4 sm:p-5 card-shadow border border-gray-100 space-y-3.5">
                <div>
                  <h3 className="font-extrabold text-sm sm:text-base text-gray-900">
                    Tabel Distribusi Target ({activeDapilMaster.nama})
                  </h3>
                  <p className="text-xs text-gray-500 font-medium">
                    Rincian distribusi target suara tingkat kecamatan. Tekan tombol &quot;Kelola Distribusi&quot; untuk mengelola target desa dan RW.
                  </p>
                </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-orange-50/50">
                      <TableHead className="font-extrabold w-12 text-center">No.</TableHead>
                      <TableHead className="font-extrabold">Kecamatan</TableHead>
                      <TableHead className="font-extrabold text-center">Desa</TableHead>
                      <TableHead className="font-extrabold text-center">RW</TableHead>
                      <TableHead className="font-extrabold text-right">DPT 2024</TableHead>
                      <TableHead className="font-extrabold text-right">Baseline 2024</TableHead>
                      <TableHead className="font-extrabold text-right bg-orange-100/40">
                        <div className="flex flex-col items-end">
                          <span>Target Kursi</span>
                          <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Top-Down</span>
                        </div>
                      </TableHead>
                      <TableHead className="font-extrabold text-right bg-emerald-50/50">
                        <div className="flex flex-col items-end">
                          <span>Agregat Target RW</span>
                          <span className="text-[9px] font-semibold text-emerald-700 uppercase tracking-tight">Bottom-Up</span>
                        </div>
                      </TableHead>
                      <TableHead className="font-extrabold text-center">Kesiapan Buffer</TableHead>
                      <TableHead className="font-extrabold text-center">Cakupan Target</TableHead>
                      <TableHead className="font-extrabold text-center">Kategori</TableHead>
                      <TableHead className="font-extrabold text-center w-36">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeDapilMaster.kecamatan.map((kec, idx) => {
                      // 1. Jumlah Desa dari master KECAMATAN_DESA
                      const curKecDesas = desaList.find(d => d.kecamatan?.toLowerCase() === kec.nama?.toLowerCase())?.desa || [];
                      const kecStats = statsKecamatan.find(k => k.kecamatan?.toLowerCase() === kec.nama?.toLowerCase());
                      const desaCount = curKecDesas.length > 0 ? curKecDesas.length : (kecStats?.desa_count || null);

                      // 2. Jumlah RW dari master/stats
                      const rwCount = kecStats?.rw_count > 0 ? kecStats.rw_count : null;

                      // 3. Saved Target di DB
                      const savedTarget = targetItems.find(it => it.kecamatan?.toLowerCase() === kec.nama?.toLowerCase() && (it.level === 'kecamatan' || !it.level));

                      // 4. DPT 2024 (dari target item atau stats DPT terupload)
                      const dptVal = savedTarget?.dpt_2024 ?? (kecStats?.dpt_count > 0 ? kecStats.dpt_count : null);

                      // 5. Baseline Suara 2024 (dari saved target atau master PPT resmi)
                      const baselineVal = savedTarget?.baseline_suara_2024 ?? kec.baseline_2024 ?? null;

                      // RW-RW yang ditargetkan di kecamatan ini
                      const targetedRwsInKec = targetItems.filter(
                        it => it.level === 'rw' &&
                              it.kecamatan?.toLowerCase() === kec.nama?.toLowerCase() &&
                              it.target_suara_2029 !== null &&
                              it.target_suara_2029 !== undefined &&
                              Number(it.target_suara_2029) > 0
                      );
                      const totalRwTargetKec = targetedRwsInKec.reduce((acc, it) => acc + (Number(it.target_suara_2029) || 0), 0);

                      // 6. Target Kursi Top-Down vs Agregat Target RW Bottom-Up
                      const topDownTarget = kec.target_2029 ?? savedTarget?.target_suara_2029 ?? null;
                      const hasTopDown = topDownTarget !== null && Number(topDownTarget) > 0;
                      const hasBottomUp = totalRwTargetKec > 0;
                      const selisih = hasTopDown && hasBottomUp ? totalRwTargetKec - Number(topDownTarget) : null;
                      const selisihPct = hasTopDown && hasBottomUp && Number(topDownTarget) > 0 ? (selisih / Number(topDownTarget)) * 100 : 0;
                      const isSurplus = selisih !== null && selisih >= 0;

                      // 7. Target Desa (jumlah desa yang sudah memiliki target atau memiliki RW bertarget)
                      const desasWithTargetedRwInKec = new Set(targetedRwsInKec.map(it => it.desa?.toLowerCase())).size;
                      const explicitDesaInKec = targetItems.filter(
                        it => it.level === 'desa' &&
                              it.kecamatan?.toLowerCase() === kec.nama?.toLowerCase() &&
                              it.target_suara_2029 !== null &&
                              it.target_suara_2029 !== undefined &&
                              Number(it.target_suara_2029) > 0
                      ).length;
                      const targetDesaCount = explicitDesaInKec > 0 ? explicitDesaInKec : desasWithTargetedRwInKec;

                      // 8. Target RW (jumlah RW yang sudah memiliki target suara > 0 tersimpan)
                      const targetRwCount = targetedRwsInKec.length;

                      return (
                        <TableRow key={kec.nama} className="hover:bg-orange-50/30 text-xs">
                          <TableCell className="font-semibold text-gray-500 text-center">{idx + 1}</TableCell>
                          <TableCell className="font-extrabold text-gray-900">{kec.nama}</TableCell>
                          <TableCell className="text-center font-bold text-gray-800">
                            {desaCount !== null ? `${desaCount} Desa` : 'N/A'}
                          </TableCell>
                          <TableCell className="text-center font-bold text-gray-800">
                            {rwCount !== null ? `${formatNumber(rwCount)} RW` : 'N/A'}
                          </TableCell>
                          <TableCell className="text-right font-medium text-gray-700">
                            {dptVal !== null ? formatNumber(dptVal) : 'N/A'}
                          </TableCell>
                          <TableCell className="text-right font-medium text-gray-700">
                            {baselineVal !== null ? formatNumber(baselineVal) : 'N/A'}
                          </TableCell>
                          <TableCell className="text-right bg-orange-50/20 font-black text-orange-600">
                            {topDownTarget !== null ? formatNumber(topDownTarget) : <span className="text-gray-400 font-semibold italic">—</span>}
                          </TableCell>
                          <TableCell className="text-right bg-emerald-50/30 font-black text-emerald-700">
                            {hasBottomUp ? formatNumber(totalRwTargetKec) : <span className="text-gray-400 font-semibold italic">—</span>}
                          </TableCell>
                          <TableCell className="text-center">
                            {selisih !== null ? (
                              <Badge
                                variant="outline"
                                className={`text-[10px] font-black ${
                                  isSurplus
                                    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                    : 'bg-amber-100 text-amber-800 border-amber-300'
                                }`}
                              >
                                {isSurplus ? `+${formatNumber(selisih)} (+${selisihPct.toFixed(0)}% Aman)` : `${formatNumber(selisih)} (${selisihPct.toFixed(0)}%)`}
                              </Badge>
                            ) : hasBottomUp ? (
                              <Badge variant="outline" className="text-[10px] font-bold bg-emerald-50 text-emerald-700 border-emerald-200">
                                {formatNumber(totalRwTargetKec)} Terisi
                              </Badge>
                            ) : (
                              <span className="text-gray-400 font-semibold italic">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="font-bold text-gray-800">
                                {targetDesaCount} / {desaCount || targetDesaCount} Desa
                              </span>
                              <span className="text-[10px] font-semibold text-orange-600">
                                {targetRwCount} / {rwCount || targetRwCount} RW
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center font-bold">
                            {(() => {
                              const kat = savedTarget?.kategori || activeDapilMaster.kategori;
                              if (!kat) return <span className="text-gray-300 font-semibold">-</span>;
                              const k = kat.toLowerCase();
                              const cls =
                                k.includes('potensial') || k.includes('basis') || k.includes('pemenang')
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                  : k.includes('menantang') || k.includes('ring 1') || k.includes('penguatan')
                                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                                  : 'bg-rose-100 text-rose-800 border-rose-300';
                              return (
                                <Badge variant="outline" className={`font-black text-[10px] ${cls}`}>
                                  {kat}
                                </Badge>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <Button
                                size="sm"
                                onClick={() => setDistLevel({ level: 'kecamatan', dapil: activeDapilMaster.dapil, kecamatan: kec.nama, desa: '' })}
                                className="h-7 text-xs px-2.5 font-bold bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
                              >
                                Kelola Distribusi
                              </Button>
                              {isAuthorized && (
                                <button
                                  onClick={() => openEditModal({
                                    level: 'kecamatan',
                                    kecamatan: kec.nama,
                                    dapil: activeDapilMaster.dapil,
                                    baseline_suara_2024: baselineVal,
                                    target_suara_2029: topDownTarget
                                  })}
                                  className="p-1.5 rounded-lg hover:bg-orange-100 text-gray-500 hover:text-orange-600 border border-gray-200"
                                  title="Atur Target Kecamatan"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* TOTAL DAPIL ROW */}
                    {(() => {
                      const c = getTargetCakupan(activeDapilMaster.dapil);
                      return (
                        <TableRow className="bg-orange-100/70 border-t-2 border-orange-300 font-black text-gray-950 text-xs">
                          <TableCell className="text-center font-black" colSpan={2}>
                            TOTAL {activeDapilMaster.nama.toUpperCase()}
                          </TableCell>
                          <TableCell className="text-center font-black">
                            {activeDapilMaster.total_desa} Desa
                          </TableCell>
                          <TableCell className="text-center font-black">
                            {formatNumber(activeDapilMaster.total_rw)} RW
                          </TableCell>
                          <TableCell className="text-right font-black">
                            {formatNumber(activeDapilMaster.dpt_2024)}
                          </TableCell>
                          <TableCell className="text-right font-black">
                            {formatNumber(activeDapilMaster.baseline_2024)}
                          </TableCell>
                          <TableCell className="text-right font-black text-orange-700 bg-orange-200/40">
                            {formatNumber(activeDapilMaster.target_suara_2029)}
                          </TableCell>
                          <TableCell className="text-right font-black text-emerald-800 bg-emerald-100/50">
                            {c.totalTargetSuaraRw > 0 ? formatNumber(c.totalTargetSuaraRw) : '—'}
                          </TableCell>
                          <TableCell className="text-center">
                            {c.totalTargetSuaraRw > 0 ? (
                              <Badge
                                variant="outline"
                                className={`text-[10px] font-black ${
                                  c.isSurplus
                                    ? 'bg-emerald-200 text-emerald-950 border-emerald-400'
                                    : 'bg-amber-200 text-amber-950 border-amber-400'
                                }`}
                              >
                                {c.isSurplus
                                  ? `+${formatNumber(c.selisihBuffer)} (+${c.bufferPct.toFixed(1)}% Aman)`
                                  : `${formatNumber(c.selisihBuffer)} (${c.bufferPct.toFixed(1)}%)`}
                              </Badge>
                            ) : (
                              <span className="text-gray-400 font-semibold italic">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center font-bold">
                            {c.targetDesa || 0}/{activeDapilMaster.total_desa} Desa · {c.targetRw || 0}/{activeDapilMaster.total_rw} RW
                          </TableCell>
                          <TableCell className="text-center font-bold text-orange-800">
                            {activeDapilMaster.kategori}
                          </TableCell>
                          <TableCell className="text-center font-bold text-orange-900">
                            {activeDapilMaster.target_kursi_2029} Kursi
                          </TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
            </div>
          )}

          {/* ========================================================== */}
          {/* TINGKAT 2: DETAIL DISTRIBUSI DESA / KELURAHAN              */}
          {/* ========================================================== */}
          {distLevel.level === 'kecamatan' && (
            <div className="space-y-4">
              {/* RINGKASAN KECAMATAN */}
              <div className="bg-white rounded-2xl p-5 card-shadow border border-gray-100 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-black text-gray-900">
                      Kecamatan {distLevel.kecamatan}
                    </h3>
                    <Badge className="bg-orange-100 text-orange-800 font-extrabold text-xs">
                      {distLevel.dapil}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 font-medium mt-1">
                    Kelola target suara desa dan RW di Kecamatan {distLevel.kecamatan}.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {isAuthorized && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const curKecMaster = activeDapilMaster.kecamatan.find(k => k.nama === distLevel.kecamatan);
                        const savedTarget = targetItems.find(it => it.kecamatan === distLevel.kecamatan && (it.level === 'kecamatan' || !it.level));
                        openEditModal({
                          level: 'kecamatan',
                          kecamatan: distLevel.kecamatan,
                          dapil: distLevel.dapil,
                          baseline_suara_2024: savedTarget?.baseline_suara_2024 ?? curKecMaster?.baseline_2024,
                          target_suara_2029: savedTarget?.target_suara_2029 ?? curKecMaster?.target_2029
                        });
                      }}
                      className="text-xs font-bold border-orange-200 text-orange-700 hover:bg-orange-50 gap-1.5 h-8"
                    >
                      <Edit2 className="w-3.5 h-3.5" /> Atur Target Kec. {distLevel.kecamatan}
                    </Button>
                  )}
                </div>
              </div>

              {/* TABEL DESA/KELURAHAN */}
              <div className="bg-white rounded-xl p-4 sm:p-5 card-shadow border border-gray-100">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-orange-50/50">
                        <TableHead className="font-extrabold w-12 text-center">No.</TableHead>
                        <TableHead className="font-extrabold">Nama Desa / Kelurahan</TableHead>
                        <TableHead className="font-extrabold text-center">Jumlah RW</TableHead>
                        <TableHead className="font-extrabold text-right">DPT 2024</TableHead>
                        <TableHead className="font-extrabold text-right">Baseline 2024</TableHead>
                        <TableHead className="font-extrabold text-right bg-orange-100/40">
                          <div className="flex flex-col items-end">
                            <span>Target Desa</span>
                            <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Top-Down</span>
                          </div>
                        </TableHead>
                        <TableHead className="font-extrabold text-right bg-emerald-50/50">
                          <div className="flex flex-col items-end">
                            <span>Agregat Target RW</span>
                            <span className="text-[9px] font-semibold text-emerald-700 uppercase tracking-tight">Bottom-Up</span>
                          </div>
                        </TableHead>
                        <TableHead className="font-extrabold text-center">Kesiapan Buffer</TableHead>
                        <TableHead className="font-extrabold text-center">Cakupan RW</TableHead>
                        <TableHead className="font-extrabold text-right">Rata-rata / RW</TableHead>
                        <TableHead className="font-extrabold text-center">Kategori</TableHead>
                        <TableHead className="font-extrabold text-center w-36">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const curKecDesas = desaList.find(d => d.kecamatan?.toLowerCase() === distLevel.kecamatan?.toLowerCase())?.desa || [];
                        if (curKecDesas.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={12} className="text-center py-8 text-gray-400 font-semibold text-xs">
                                Belum ada data desa terdaftar di master wilayah untuk kecamatan ini.
                              </TableCell>
                            </TableRow>
                          );
                        }

                        let totalKecDpt = 0;
                        let totalKecBaseline = 0;
                        let totalKecTopDown = 0;
                        let totalKecBottomUp = 0;
                        let totalKecRw = 0;
                        let totalKecRwBertarget = 0;

                        const rows = curKecDesas.map((dObj, idx) => {
                          const desaName = typeof dObj === 'string' ? dObj : dObj.nama;
                          const desaStatus = typeof dObj === 'object' ? dObj.status : 'Desa';
                          const normDesa = (s) => String(s || '').toLowerCase().replace(/[\s_\-]+/g, '');
                          const desaStats = statsDesa.find(d =>
                            d.kecamatan?.toLowerCase() === distLevel.kecamatan?.toLowerCase() &&
                            normDesa(d.desa) === normDesa(desaName)
                          );
                          const rwCount = desaStats?.rw_count > 0 ? desaStats.rw_count : null;
                          if (rwCount) totalKecRw += rwCount;

                          const savedRwTargetsInDesa = targetItems.filter(
                            it => it.level === 'rw' &&
                                  it.kecamatan?.toLowerCase() === distLevel.kecamatan?.toLowerCase() &&
                                  normDesa(it.desa) === normDesa(desaName) &&
                                  it.target_suara_2029 !== null &&
                                  it.target_suara_2029 !== undefined &&
                                  Number(it.target_suara_2029) > 0
                          );
                          const rwTargetCount = savedRwTargetsInDesa.length;
                          totalKecRwBertarget += rwTargetCount;

                          const totalTargetRwSuara = savedRwTargetsInDesa.reduce((acc, it) => acc + (Number(it.target_suara_2029) || 0), 0);
                          totalKecBottomUp += totalTargetRwSuara;

                          const savedTarget = targetItems.find(
                            it => it.level === 'desa' &&
                                  it.kecamatan?.toLowerCase() === distLevel.kecamatan?.toLowerCase() &&
                                  normDesa(it.desa) === normDesa(desaName)
                          );
                          const dptVal = savedTarget?.dpt_2024 ?? (desaStats?.dpt_count > 0 ? desaStats.dpt_count : null);
                          if (dptVal) totalKecDpt += dptVal;

                          const baselineVal = savedTarget?.baseline_suara_2024 ?? null;
                          if (baselineVal) totalKecBaseline += baselineVal;

                          const topDownVal = savedTarget?.target_suara_2029 !== undefined && savedTarget?.target_suara_2029 !== null
                            ? Number(savedTarget.target_suara_2029)
                            : null;
                          if (topDownVal) totalKecTopDown += topDownVal;

                          const hasTopDown = topDownVal !== null && topDownVal > 0;
                          const hasBottomUp = totalTargetRwSuara > 0;
                          const selisih = hasTopDown && hasBottomUp ? totalTargetRwSuara - topDownVal : null;
                          const selisihPct = hasTopDown && hasBottomUp && topDownVal > 0 ? (selisih / topDownVal) * 100 : 0;
                          const isSurplus = selisih !== null && selisih >= 0;

                          return (
                            <TableRow key={desaName} className="hover:bg-orange-50/30 text-xs">
                              <TableCell className="font-semibold text-gray-500 text-center">{idx + 1}</TableCell>
                              <TableCell className="font-bold text-gray-900">
                                <div className="flex items-center gap-2">
                                  <span>{desaName}</span>
                                  {desaStatus === 'Kelurahan' && (
                                    <Badge variant="outline" className="text-[10px] font-semibold">Kelurahan</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center font-bold text-gray-800">
                                {rwCount !== null ? `${rwCount} RW` : 'N/A'}
                              </TableCell>
                              <TableCell className="text-right font-medium text-gray-700">
                                {dptVal !== null ? formatNumber(dptVal) : 'N/A'}
                              </TableCell>
                              <TableCell className="text-right font-medium text-gray-700">
                                {baselineVal !== null ? formatNumber(baselineVal) : 'N/A'}
                              </TableCell>
                              <TableCell className="text-right bg-orange-50/20 font-black text-orange-600">
                                {topDownVal !== null ? formatNumber(topDownVal) : <span className="text-gray-400 font-semibold italic">—</span>}
                              </TableCell>
                              <TableCell className="text-right bg-emerald-50/30 font-black text-emerald-700">
                                {hasBottomUp ? formatNumber(totalTargetRwSuara) : <span className="text-gray-400 font-semibold italic">—</span>}
                              </TableCell>
                              <TableCell className="text-center">
                                {selisih !== null ? (
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] font-black ${
                                      isSurplus
                                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                        : 'bg-amber-100 text-amber-800 border-amber-300'
                                    }`}
                                  >
                                    {isSurplus ? `+${formatNumber(selisih)} (+${selisihPct.toFixed(0)}% Aman)` : `${formatNumber(selisih)} (${selisihPct.toFixed(0)}%)`}
                                  </Badge>
                                ) : hasBottomUp ? (
                                  <Badge variant="outline" className="text-[10px] font-bold bg-emerald-50 text-emerald-700 border-emerald-200">
                                    {formatNumber(totalTargetRwSuara)} Terisi
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400 font-semibold italic">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`font-bold ${rwTargetCount > 0 ? 'text-orange-700' : 'text-gray-400 italic'}`}>
                                  {rwTargetCount} / {rwCount || rwTargetCount || 0} RW
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                {rwTargetCount > 0 ? (
                                  <span className="font-black text-orange-600">
                                    {formatNumber(Math.round(totalTargetRwSuara / rwTargetCount))}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 font-semibold italic">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center font-bold">
                                {(() => {
                                  const curKecSavedTarget = targetItems.find(it => it.kecamatan?.toLowerCase() === distLevel.kecamatan?.toLowerCase() && (it.level === 'kecamatan' || !it.level));
                                  const kat = savedTarget?.kategori || curKecSavedTarget?.kategori || activeDapilMaster.kategori;
                                  if (!kat) return <span className="text-gray-300 font-semibold">-</span>;
                                  const k = kat.toLowerCase();
                                  const cls =
                                    k.includes('potensial') || k.includes('basis') || k.includes('pemenang')
                                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                      : k.includes('menantang') || k.includes('ring 1') || k.includes('penguatan')
                                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                                      : 'bg-rose-100 text-rose-800 border-rose-300';
                                  return (
                                    <Badge variant="outline" className={`font-black text-[10px] ${cls}`}>
                                      {kat}
                                    </Badge>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1.5">
                                  <Button
                                    size="sm"
                                    onClick={() => setDistLevel({
                                      level: 'desa',
                                      dapil: distLevel.dapil,
                                      kecamatan: distLevel.kecamatan,
                                      desa: desaName
                                    })}
                                    className="h-7 text-xs px-2.5 font-bold bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
                                  >
                                    Detail RW
                                  </Button>
                                  {isAuthorized && (
                                    <button
                                      onClick={() => openEditModal({
                                        level: 'desa',
                                        kecamatan: distLevel.kecamatan,
                                        desa: desaName,
                                        dapil: distLevel.dapil,
                                        baseline_suara_2024: baselineVal,
                                        target_suara_2029: topDownVal
                                      })}
                                      className="p-1.5 rounded-lg hover:bg-orange-100 text-gray-500 hover:text-orange-600 border border-gray-200"
                                      title="Atur Target Desa"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        });

                        // TOTAL KECAMATAN ROW
                        const curKecMaster = activeDapilMaster.kecamatan.find(k => k.nama?.toLowerCase() === distLevel.kecamatan?.toLowerCase());
                        const kecPlanTarget = curKecMaster?.target_2029 || totalKecTopDown || 0;
                        const kecBuffer = totalKecBottomUp - kecPlanTarget;
                        const kecBufferPct = kecPlanTarget > 0 ? (kecBuffer / kecPlanTarget) * 100 : 0;
                        const isKecSurplus = kecBuffer >= 0;

                        return (
                          <>
                            {rows}
                            <TableRow className="bg-orange-100/70 border-t-2 border-orange-300 font-black text-gray-950 text-xs">
                              <TableCell className="text-center font-black" colSpan={2}>
                                TOTAL KEC. {distLevel.kecamatan?.toUpperCase()}
                              </TableCell>
                              <TableCell className="text-center font-black">
                                {formatNumber(totalKecRw)} RW
                              </TableCell>
                              <TableCell className="text-right font-black">
                                {formatNumber(totalKecDpt || curKecMaster?.dpt_2024 || 0)}
                              </TableCell>
                              <TableCell className="text-right font-black">
                                {formatNumber(totalKecBaseline || curKecMaster?.baseline_2024 || 0)}
                              </TableCell>
                              <TableCell className="text-right font-black text-orange-700 bg-orange-200/40">
                                {formatNumber(kecPlanTarget)}
                              </TableCell>
                              <TableCell className="text-right font-black text-emerald-800 bg-emerald-100/50">
                                {totalKecBottomUp > 0 ? formatNumber(totalKecBottomUp) : '—'}
                              </TableCell>
                              <TableCell className="text-center">
                                {totalKecBottomUp > 0 && kecPlanTarget > 0 ? (
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] font-black ${
                                      isKecSurplus
                                        ? 'bg-emerald-200 text-emerald-950 border-emerald-400'
                                        : 'bg-amber-200 text-amber-950 border-amber-400'
                                    }`}
                                  >
                                    {isKecSurplus
                                      ? `+${formatNumber(kecBuffer)} (+${kecBufferPct.toFixed(1)}% Aman)`
                                      : `${formatNumber(kecBuffer)} (${kecBufferPct.toFixed(1)}%)`}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400 font-semibold italic">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center font-bold">
                                {totalKecRwBertarget} / {totalKecRw} RW
                              </TableCell>
                              <TableCell className="text-right font-bold text-orange-800">
                                ~{totalKecRwBertarget > 0 ? formatNumber(Math.round(totalKecBottomUp / totalKecRwBertarget)) : 0}
                              </TableCell>
                              <TableCell className="text-center font-bold text-orange-800">
                                {curKecMaster?.kategori || activeDapilMaster.kategori}
                              </TableCell>
                              <TableCell className="text-center font-bold text-gray-500">
                                {curKecDesas.length} Desa
                              </TableCell>
                            </TableRow>
                          </>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================== */}
          {/* TINGKAT 3: DETAIL DISTRIBUSI RW                            */}
          {/* ========================================================== */}
          {distLevel.level === 'desa' && (() => {
            const curKec = distLevel.kecamatan;
            const curDesa = distLevel.desa;
            const curDapil = distLevel.dapil;

            const normDesa = (s) => String(s || '').toLowerCase().replace(/[\s_\-]+/g, '');
            const desaStats = statsDesa.find(d =>
              d.kecamatan?.toLowerCase() === curKec?.toLowerCase() &&
              normDesa(d.desa) === normDesa(curDesa)
            );
            const rwMasterCount = desaStats?.rw_count > 0 ? desaStats.rw_count : null;

            const savedDesaTarget = targetItems.find(
              it => it.level === 'desa' &&
                    it.kecamatan?.toLowerCase() === curKec?.toLowerCase() &&
                    normDesa(it.desa) === normDesa(curDesa)
            );
            const desaTargetVal = savedDesaTarget?.target_suara_2029 ?? null;
            const desaBaselineVal = savedDesaTarget?.baseline_suara_2024 ?? null;

            const savedRwTargets = targetItems.filter(
              it => it.level === 'rw' &&
                    it.kecamatan?.toLowerCase() === curKec?.toLowerCase() &&
                    normDesa(it.desa) === normDesa(curDesa)
            );
            const savedRwWithTarget = savedRwTargets.filter(
              it => it.target_suara_2029 !== null && it.target_suara_2029 !== undefined && Number(it.target_suara_2029) > 0
            );
            const rwBertargetCount = savedRwWithTarget.length;
            const totalRwTargetSuara = savedRwWithTarget.reduce(
              (acc, it) => acc + (Number(it.target_suara_2029) || 0),
              0
            );

            // Kumpulkan semua RW dari target tersimpan dan data realisasi/stats
            const knownRwsFromStats = rwRealisasiList.filter(
              r => r.kecamatan?.toLowerCase() === curKec?.toLowerCase() &&
                   normDesa(r.desa) === normDesa(curDesa)
            );

            const allRwMap = new window.Map();

            savedRwTargets.forEach(t => {
              if (t.rw) {
                const rwKey = normalizeRwName(t.rw);
                allRwMap.set(rwKey, {
                  rw: rwKey,
                  dpt: t.dpt_2024 ?? null,
                  baseline: t.baseline_suara_2024 ?? null,
                  target: t.target_suara_2029 ?? null,
                  kategori: t.kategori || '',
                  targetItem: t,
                  statsItem: null,
                });
              }
            });

            knownRwsFromStats.forEach(r => {
              const rwKey = normalizeRwName(r.rw);
              const existing = allRwMap.get(rwKey);
              const rDpt = (r.dpt_count > 0 ? r.dpt_count : (r.dpt > 0 ? r.dpt : null));
              if (existing) {
                if (existing.dpt === null || existing.dpt === undefined || existing.dpt === 0) {
                  existing.dpt = rDpt;
                }
                existing.baseline = existing.baseline ?? r.baseline ?? null;
                existing.statsItem = r;
              } else {
                allRwMap.set(rwKey, {
                  rw: rwKey,
                  dpt: rDpt,
                  baseline: r.baseline ?? null,
                  target: null,
                  kategori: '',
                  targetItem: null,
                  statsItem: r,
                });
              }
            });

            const rwRows = Array.from(allRwMap.values()).sort((a, b) =>
              a.rw.localeCompare(b.rw, undefined, { numeric: true })
            );

            return (
              <div className="space-y-4">
                {/* BREADCRUMB IDENTITAS WILAYAH */}
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 flex-wrap">
                  <button
                    onClick={() => switchTab('basis_data')}
                    className="hover:text-orange-600 transition-colors"
                  >
                    Kabupaten Sukabumi
                  </button>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  <button
                    onClick={() => setDistLevel({ level: 'dapil', dapil: curDapil, kecamatan: '', desa: '' })}
                    className="hover:text-orange-600 transition-colors"
                  >
                    {curDapil}
                  </button>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  <button
                    onClick={() => setDistLevel({ level: 'kecamatan', dapil: curDapil, kecamatan: curKec, desa: '' })}
                    className="hover:text-orange-600 transition-colors"
                  >
                    Kecamatan {curKec}
                  </button>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-orange-600 font-bold">Desa {curDesa}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-gray-900 font-extrabold bg-orange-100/70 px-2 py-0.5 rounded text-[11px]">
                    RW
                  </span>
                </div>

                {/* HEADER DESA & 3 TOMBOL AKSI UTAMA */}
                <div className="bg-white rounded-xl p-3.5 sm:p-4 card-shadow border border-gray-100 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">
                      Desa {curDesa}
                    </h2>
                    <p className="text-xs font-bold text-gray-500 mt-0.5">
                      Kecamatan {curKec} · {curDapil}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {isAuthorized && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal({
                          level: 'desa',
                          kecamatan: curKec,
                          desa: curDesa,
                          dapil: curDapil,
                          baseline_suara_2024: desaBaselineVal,
                          target_suara_2029: desaTargetVal,
                          isNew: false
                        })}
                        className="text-xs font-bold border-orange-200 text-orange-700 hover:bg-orange-50 gap-1.5 h-8.5"
                      >
                        <Edit2 className="w-3.5 h-3.5" /> Atur Target Desa
                      </Button>
                    )}

                    {isAuthorized && (
                      <Button
                        size="sm"
                        onClick={() => openEditModal({
                          level: 'rw',
                          kecamatan: curKec,
                          desa: curDesa,
                          dapil: curDapil,
                          rw: '',
                          baseline_suara_2024: '',
                          target_suara_2029: '',
                          kategori: '',
                          isNew: true
                        })}
                        className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold gap-1.5 h-8.5 shadow-sm"
                      >
                        <Plus className="w-3.5 h-3.5" /> + Tambah Target RW
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/partisipan?kecamatan=${encodeURIComponent(curKec)}&desa=${encodeURIComponent(curDesa)}`)}
                      className="text-xs font-bold border-gray-200 text-gray-700 hover:bg-gray-50 gap-1.5 h-8.5"
                    >
                      <Users className="w-3.5 h-3.5 text-gray-500" /> Lihat Data Orang
                    </Button>
                  </div>
                </div>

                {/* 5 KARTU RINGKASAN */}
                {(() => {
                  const totalRwBaselineSuara = rwRows.reduce((acc, r) => acc + (Number(r.baseline) || 0), 0);
                  const effectiveDesaBaseline = totalRwBaselineSuara > 0 ? totalRwBaselineSuara : desaBaselineVal;
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                      {/* Kartu 1: Jumlah RW */}
                      <div className="bg-white rounded-xl p-3.5 sm:p-4 card-shadow border border-gray-100 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Jumlah RW</p>
                          <p className="text-xl sm:text-2xl font-black text-gray-900 mt-0.5">
                            {rwMasterCount !== null ? `${rwMasterCount} RW` : '—'}
                          </p>
                          <p className="text-[10px] text-gray-400 font-semibold mt-0.5">Master wilayah valid</p>
                        </div>
                        <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center font-bold">
                          <Layers className="w-4.5 h-4.5" />
                        </div>
                      </div>

                      {/* Kartu 2: RW Bertarget */}
                      <div className="bg-white rounded-xl p-3.5 sm:p-4 card-shadow border border-gray-100 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">RW Bertarget</p>
                          <p className="text-xl sm:text-2xl font-black text-orange-600 mt-0.5">
                            {rwBertargetCount} RW
                          </p>
                          <p className="text-[10px] text-gray-400 font-semibold mt-0.5">Target tersimpan</p>
                        </div>
                        <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                          <Target className="w-4.5 h-4.5" />
                        </div>
                      </div>

                      {/* Kartu 3: Baseline Suara 2024 */}
                      <div className="bg-white rounded-xl p-3.5 sm:p-4 card-shadow border border-blue-100 bg-blue-50/20 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider">Baseline Suara 2024</p>
                          <p className={`text-xl sm:text-2xl font-black mt-0.5 ${effectiveDesaBaseline !== null && effectiveDesaBaseline !== undefined ? 'text-blue-900' : 'text-gray-400 italic text-sm'}`}>
                            {effectiveDesaBaseline !== null && effectiveDesaBaseline !== undefined ? formatNumber(effectiveDesaBaseline) : 'Belum ada data'}
                          </p>
                          <p className="text-[10px] text-blue-600 font-semibold mt-0.5">Suara Pemilu 2024</p>
                        </div>
                        <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                          <Flag className="w-4.5 h-4.5" />
                        </div>
                      </div>

                      {/* Kartu 4: Target Suara Desa 2029 */}
                      <div className="bg-white rounded-xl p-3.5 sm:p-4 card-shadow border border-orange-100 bg-orange-50/20 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-bold text-orange-800 uppercase tracking-wider">Target Desa (Top-Down)</p>
                          <p className={`text-xl sm:text-2xl font-black mt-0.5 ${desaTargetVal !== null && desaTargetVal !== undefined ? 'text-orange-600' : 'text-gray-400 italic text-sm'}`}>
                            {desaTargetVal !== null && desaTargetVal !== undefined ? formatNumber(desaTargetVal) : 'Belum ditetapkan'}
                          </p>
                          <p className="text-[10px] text-orange-600 font-semibold mt-0.5">Target rencana desa</p>
                        </div>
                        <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center font-bold">
                          <Target className="w-4.5 h-4.5" />
                        </div>
                      </div>

                      {/* Kartu 5: Total Target Suara RW */}
                      <div className="bg-white rounded-xl p-3.5 sm:p-4 card-shadow border border-emerald-100 bg-emerald-50/20 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Agregat RW (Bottom-Up)</p>
                          <p className="text-xl sm:text-2xl font-black text-emerald-700 mt-0.5">
                            {formatNumber(totalRwTargetSuara)}
                          </p>
                          <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">Akumulasi target RW</p>
                        </div>
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                          <TrendingUp className="w-4.5 h-4.5" />
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* TABEL DETAIL RW */}
                <div className="bg-white rounded-xl p-4 sm:p-5 card-shadow border border-gray-100">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-orange-50/50">
                          <TableHead className="font-extrabold w-12 text-center">No.</TableHead>
                          <TableHead className="font-extrabold">RW</TableHead>
                          <TableHead className="font-extrabold text-center">RT</TableHead>
                          <TableHead className="font-extrabold text-center">TPS</TableHead>
                          <TableHead className="font-extrabold text-right">DPT 2024</TableHead>
                          <TableHead className="font-extrabold text-right bg-blue-50/50 text-blue-900">Baseline 2024</TableHead>
                          <TableHead className="font-extrabold text-right bg-orange-50/50 text-orange-900">Target Suara 2029</TableHead>
                          <TableHead className="font-extrabold text-right">Pelopor</TableHead>
                          <TableHead className="font-extrabold text-right">Non Pelopor</TableHead>
                          <TableHead className="font-extrabold text-right">Saksi</TableHead>
                          <TableHead className="font-extrabold text-right">Relawan</TableHead>
                          <TableHead className="font-extrabold text-right bg-orange-100">Total Unik</TableHead>
                          <TableHead className="font-extrabold text-center">DPC</TableHead>
                          <TableHead className="font-extrabold text-center">DPRa</TableHead>
                          <TableHead className="font-extrabold text-center">Angg. Pelopor</TableHead>
                          <TableHead className="font-extrabold text-center">RKI</TableHead>
                          <TableHead className="font-extrabold text-right">Realisasi</TableHead>
                          <TableHead className="font-extrabold">Progress</TableHead>
                          <TableHead className="font-extrabold text-center">Status Pendataan</TableHead>
                          <TableHead className="font-extrabold text-center">Kategori RW</TableHead>
                          <TableHead className="font-extrabold text-center w-36">Aksi</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          if (rwLoading) {
                            return (
                              <TableRow>
                                <TableCell colSpan={21} className="text-center py-8 text-gray-500 font-semibold text-xs">
                                  Memuat data rincian RW...
                                </TableCell>
                              </TableRow>
                            );
                          }

                          if (rwRows.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={21} className="text-center py-8 text-gray-400 font-semibold text-xs">
                                  Belum ada data RW yang terdata di Desa {curDesa}. Klik &quot;+ Tambah Target RW&quot; di atas untuk menetapkan target suara manual per RW.
                                </TableCell>
                              </TableRow>
                            );
                          }

                          let sumRt = 0;
                          let sumTps = 0;
                          let sumDpt = 0;
                          let sumBaseline = 0;
                          let sumTarget = 0;
                          let sumPelopor = 0;
                          let sumNonPelopor = 0;
                          let sumSaksi = 0;
                          let sumRelawan = 0;
                          let sumTotalUnik = 0;
                          let sumRealisasi = 0;

                          const renderedRows = rwRows.map((row, idx) => {
                            const statsItem = row.statsItem;
                            const hasStats = Boolean(statsItem);

                            const peloporCount = hasStats ? (statsItem.pelopor_count || 0) : 0;
                            const nonPeloporCount = hasStats ? (statsItem.non_pelopor_count || 0) : 0;
                            const saksiCount = hasStats ? (statsItem.saksi || 0) : 0;
                            const relawanCount = hasStats ? (statsItem.simpatisan || 0) : 0;
                            const totalUnik = hasStats ? (statsItem.total_unik || statsItem.total || 0) : 0;
                            const dpcCount = hasStats ? (statsItem.dpc || 0) : 0;
                            const dpraCount = hasStats ? (statsItem.dpra || 0) : 0;
                            const anggPeloporCount = hasStats ? (statsItem.pelopor || 0) : 0;
                            const rkiCount = hasStats ? (statsItem.rki || 0) : 0;
                            const rtCount = hasStats ? (statsItem.rt_count || 0) : 0;
                            const tpsCount = hasStats ? (statsItem.tps_count || 0) : 0;
                            const realisasiVal = hasStats ? (statsItem.realisasi !== undefined ? statsItem.realisasi : statsItem.total || 0) : 0;

                            const targetVal = row.target !== null && row.target !== undefined && Number(row.target) > 0 ? Number(row.target) : (statsItem?.target || 0);
                            const isTargetSet = targetVal > 0;
                            const hasPerson = hasStats && totalUnik > 0;
                            const pct = targetVal > 0 ? Math.round((realisasiVal / targetVal) * 100) : 0;

                            sumRt += rtCount;
                            sumTps += tpsCount;
                            if (row.dpt) sumDpt += Number(row.dpt);
                            if (row.baseline) sumBaseline += Number(row.baseline);
                            if (targetVal) sumTarget += Number(targetVal);
                            sumPelopor += peloporCount;
                            sumNonPelopor += nonPeloporCount;
                            sumSaksi += saksiCount;
                            sumRelawan += relawanCount;
                            sumTotalUnik += totalUnik;
                            sumRealisasi += realisasiVal;

                            // Status Pendataan: 4 Kategori Eksak
                            let statusText = 'Belum lengkap';
                            let statusBadgeClass = 'bg-slate-100 text-slate-700 border-slate-300';
                            if (isTargetSet && hasPerson) {
                              statusText = 'Lengkap';
                              statusBadgeClass = 'bg-emerald-100 text-emerald-800 border-emerald-300';
                            } else if (!isTargetSet && hasPerson) {
                              statusText = 'Target belum ditetapkan';
                              statusBadgeClass = 'bg-amber-100 text-amber-800 border-amber-300';
                            } else if (isTargetSet && !hasPerson) {
                              statusText = 'Data perlu dilengkapi';
                              statusBadgeClass = 'bg-sky-100 text-sky-800 border-sky-300';
                            } else {
                              statusText = 'Belum lengkap';
                              statusBadgeClass = 'bg-slate-100 text-slate-700 border-slate-300';
                            }

                            return (
                              <TableRow key={row.rw} className="hover:bg-orange-50/30 text-xs">
                                <TableCell className="font-semibold text-gray-500 text-center">{idx + 1}</TableCell>
                                <TableCell className="font-extrabold text-gray-900">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-100 text-orange-700 font-bold">
                                    <MapPinIcon className="w-3 h-3" /> {normalizeRwName(row.rw)}
                                  </span>
                                  {normalizeRwName(row.rw) === 'RW 000' && (
                                    <span className="ml-1.5 text-[10px] text-gray-400 font-medium italic block sm:inline">
                                      (Data DPT Belum Ber-RW)
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center font-semibold text-gray-600">{rtCount}</TableCell>
                                <TableCell className="text-center font-semibold text-gray-600">{tpsCount}</TableCell>
                                <TableCell className="text-right font-medium text-gray-700">
                                  {row.dpt !== null && row.dpt !== undefined && row.dpt > 0 ? (
                                    formatNumber(row.dpt)
                                  ) : (
                                    <span className="text-gray-300 font-semibold">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right bg-blue-50/30 font-bold text-blue-900">
                                  {row.baseline !== null && row.baseline !== undefined && Number(row.baseline) > 0 ? (
                                    formatNumber(row.baseline)
                                  ) : (
                                    <span className="text-gray-300 font-semibold">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right bg-orange-50/20 font-black text-orange-600">
                                  {isTargetSet ? (
                                    <span className="font-black text-orange-600 text-sm">{formatNumber(targetVal)}</span>
                                  ) : (
                                    <span className="text-gray-400 font-semibold italic text-xs">Belum ditetapkan</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-bold text-amber-600">{peloporCount}</TableCell>
                                <TableCell className="text-right font-bold text-orange-600">{nonPeloporCount}</TableCell>
                                <TableCell className="text-right font-medium text-emerald-600">{saksiCount}</TableCell>
                                <TableCell className="text-right font-medium text-blue-600">{relawanCount}</TableCell>
                                <TableCell className="text-right font-extrabold text-orange-600 bg-orange-50/30">{totalUnik}</TableCell>
                                <TableCell className="text-center">{dpcCount > 0 ? <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{dpcCount}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                                <TableCell className="text-center">{dpraCount > 0 ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{dpraCount}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                                <TableCell className="text-center">{anggPeloporCount > 0 ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{anggPeloporCount}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                                <TableCell className="text-center">{rkiCount > 0 ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{rkiCount}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                                <TableCell className="text-right font-bold text-orange-600">{formatNumber(realisasiVal)}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex-1 min-w-[50px]">
                                      <div className="h-full bg-gradient-to-r from-orange-400 to-orange-500" style={{width: `${Math.min(100, pct)}%`}}></div>
                                    </div>
                                    <span className="text-xs font-extrabold text-orange-600 w-8">{pct}%</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline" className={`text-[10px] font-extrabold border ${statusBadgeClass}`}>
                                    {statusText}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center font-bold">
                                  {(() => {
                                    const kat = row.kategori;
                                    if (!kat) return <span className="text-gray-300 font-semibold">-</span>;
                                    const k = kat.toLowerCase();
                                    const cls =
                                      k.includes('utama') || k.includes('basis') || k.includes('potensial')
                                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                        : k.includes('menengah') || k.includes('kompetitif')
                                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                                        : k.includes('rendah')
                                        ? 'bg-sky-100 text-sky-800 border-sky-300'
                                        : 'bg-slate-100 text-slate-700 border-slate-300';
                                    return (
                                      <Badge variant="outline" className={`${cls} text-[10px] font-bold`}>
                                        {kat}
                                      </Badge>
                                    );
                                  })()}
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => navigate(`/partisipan?kecamatan=${encodeURIComponent(curKec)}&desa=${encodeURIComponent(curDesa)}&rw=${encodeURIComponent(row.rw)}`)}
                                      className="h-7 text-xs px-2 font-bold text-gray-700 hover:text-orange-700 hover:bg-orange-50 gap-1 border border-gray-200"
                                      title="Buka Basis Data Orang terfilter ke RW ini"
                                    >
                                      <Users className="w-3 h-3" /> Lihat
                                    </Button>
                                    {isAuthorized && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openEditModal({
                                          level: 'rw',
                                          kecamatan: curKec,
                                          desa: curDesa,
                                          dapil: curDapil,
                                          rw: row.rw,
                                          baseline_suara_2024: row.baseline,
                                          target_suara_2029: row.target,
                                          kategori: row.kategori,
                                          isNew: false
                                        })}
                                        className="h-7 text-xs px-2 font-bold border-orange-200 text-orange-700 hover:bg-orange-50 gap-1"
                                      >
                                        <Edit2 className="w-3 h-3" /> Edit
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          });

                          return (
                            <>
                              {renderedRows}
                              <TableRow className="bg-orange-100/70 border-t-2 border-orange-300 font-black text-gray-950 text-xs">
                                <TableCell className="text-center font-black" colSpan={2}>
                                  TOTAL DESA {curDesa?.toUpperCase()}
                                </TableCell>
                                <TableCell className="text-center font-black">{sumRt}</TableCell>
                                <TableCell className="text-center font-black">{sumTps}</TableCell>
                                <TableCell className="text-right font-black">{formatNumber(sumDpt)}</TableCell>
                                <TableCell className="text-right font-black text-blue-900 bg-blue-100/50">{formatNumber(sumBaseline)}</TableCell>
                                <TableCell className="text-right font-black text-orange-700 bg-orange-200/40">{formatNumber(sumTarget)}</TableCell>
                                <TableCell className="text-right font-black">{sumPelopor}</TableCell>
                                <TableCell className="text-right font-black">{sumNonPelopor}</TableCell>
                                <TableCell className="text-right font-black">{sumSaksi}</TableCell>
                                <TableCell className="text-right font-black">{sumRelawan}</TableCell>
                                <TableCell className="text-right font-black bg-orange-200">{sumTotalUnik}</TableCell>
                                <TableCell className="text-center" colSpan={4}>-</TableCell>
                                <TableCell className="text-right font-black">{sumRealisasi}</TableCell>
                                <TableCell className="text-center font-black" colSpan={3}>
                                  {sumTarget > 0 ? `${Math.round((sumRealisasi / sumTarget) * 100)}%` : '0%'}
                                </TableCell>
                                <TableCell className="text-center font-bold text-gray-500">{rwRows.length} RW</TableCell>
                              </TableRow>
                            </>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ============================================================== */}
      {/* VIEW 3: DISTRIBUSI TARGET DI PETA WILAYAH (SESUAI GAMBAR 3) */}
      {/* ============================================================== */}
      {activeTab === 'peta_target' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 card-shadow border border-gray-100">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h3 className="font-extrabold text-lg text-gray-900">
                  Distribusi Target Suara di Peta Wilayah
                </h3>
                <p className="text-xs text-gray-500 font-medium">
                  Intensitas target suara dan kategori zona strategis di seluruh Kabupaten Sukabumi
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-800">Potensial (Dapil 4)</Badge>
                <Badge className="bg-amber-100 text-amber-800">Menantang (Dapil 3)</Badge>
                <Badge className="bg-blue-100 text-blue-800">Perjuangan (Dapil 1, 2, 5, 6)</Badge>
              </div>
            </div>

            {/* Peta Interaktif OpenStreetMap 6 Dapil Sukabumi */}
            <div className="mt-3 mb-6">
              <PetaDapilLeaflet
                height={360}
                showControls={true}
                showLegend={true}
                onSelectDapil={(dapil) => {
                  switchTab('distribusi', dapil);
                  setDistLevel({ level: 'dapil', dapil, kecamatan: '', desa: '' });
                }}
              />
            </div>

            {/* Skema Peta Grid 6 Dapil Interaktif */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {MASTER_DAPIL_DATA.map((d) => {
                const colorInfo = DAPIL_COLORS[d.dapil] || DAPIL_COLORS['Dapil 1'];
                return (
                  <div
                    key={d.dapil}
                    className="p-5 rounded-2xl border transition-all card-hover"
                    style={{ backgroundColor: colorInfo.bg, borderColor: colorInfo.border }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: colorInfo.fill }}></span>
                        <h4 className="font-extrabold text-base text-gray-900">{d.nama}</h4>
                      </div>
                      <Badge className="bg-white/80 font-black text-gray-800 text-xs">
                        {d.target_kursi_2029} Kursi
                      </Badge>
                    </div>

                    <p className="text-xs text-gray-600 font-semibold mb-3">
                      {d.kecamatan_count} Kecamatan · {d.desa_count} Desa · {formatNumber(d.rw_count)} RW
                    </p>

                    <div className="bg-white/90 rounded-xl p-3 border border-gray-100 space-y-2 text-xs mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-medium">Target Suara 2029:</span>
                        <span className="font-black text-orange-600">{formatNumber(d.target_suara_2029)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-medium">Baseline 2024:</span>
                        <span className="font-bold text-gray-700">{formatNumber(d.baseline_2024)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 font-medium">Gap Suara:</span>
                        <span className="font-black text-amber-700">+{formatNumber(d.gap_suara)}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-200/50">
                      <span className="text-[11px] font-bold text-gray-600">Kategori: {d.kategori}</span>
                      <Button
                        size="sm"
                        onClick={() => {
                          switchTab('distribusi', d.dapil);
                          setDistLevel({ level: 'dapil', dapil: d.dapil, kecamatan: '', desa: '' });
                        }}
                        className="h-8 text-xs font-bold bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        Lihat Distribusi →
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* VIEW 4: SIMULATOR SAINTE-LAGUË & AMBANG KURSI 2029 */}
      {/* ============================================================== */}
      {activeTab === 'simulasi' && (
        <SainteLagueSimulator initialDapil={selectedDapilName || 'Dapil 3'} />
      )}

      {/* DIALOG MODAL INPUT MANUAL TARGET & BASELINE */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="font-extrabold text-base text-gray-900 flex items-center gap-2">
                {editForm.level === 'desa' ? (
                  <>
                    <Edit2 className="w-4 h-4 text-orange-600" />
                    Atur Target Suara Desa
                  </>
                ) : editForm.level === 'rw' ? (
                  <>
                    {editForm.isNew ? <Plus className="w-4 h-4 text-orange-600" /> : <Edit2 className="w-4 h-4 text-orange-600" />}
                    {editForm.isNew ? 'Tambah Target Suara RW' : `Edit Target Suara ${editForm.rw}`}
                  </>
                ) : (
                  `Atur Target (${editForm.level.toUpperCase()})`
                )}
              </h3>
              <button
                type="button"
                onClick={requestCloseModal}
                className="text-gray-400 hover:text-gray-600 font-bold p-1 rounded-lg hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              {/* Identitas Wilayah */}
              <div className="bg-orange-50/60 rounded-xl p-3 border border-orange-100 space-y-1.5">
                {editForm.level === 'desa' ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-semibold">Nama Desa:</span>
                      <span className="font-extrabold text-gray-900">{editForm.desa}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-semibold">Kecamatan:</span>
                      <span className="font-bold text-gray-800">{editForm.kecamatan}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-semibold">Dapil:</span>
                      <span className="font-bold text-orange-700">{editForm.dapil}</span>
                    </div>
                  </>
                ) : editForm.level === 'rw' ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-semibold">Wilayah:</span>
                      <span className="font-bold text-gray-800">
                        Desa {editForm.desa}, Kec. {editForm.kecamatan}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-semibold">Dapil:</span>
                      <span className="font-bold text-orange-700">{editForm.dapil}</span>
                    </div>
                  </>
                ) : (
                  <div>
                    <span className="text-gray-500 font-semibold">Wilayah:</span>{' '}
                    <span className="font-extrabold text-orange-600">
                      {[editForm.dapil, editForm.kecamatan, editForm.desa, editForm.rw].filter(Boolean).join(' → ') || 'Kabupaten Sukabumi'}
                    </span>
                  </div>
                )}
              </div>

              {/* Input Nomor RW (Khusus Level RW) */}
              {editForm.level === 'rw' && (
                <div>
                  <label className="block text-gray-700 font-bold mb-1">
                    Nama / Nomor RW <span className="text-rose-500">*</span>
                  </label>
                  {editForm.isNew ? (
                    <div className="space-y-2">
                      {(() => {
                        const savedRwsInDesa = new Set(
                          targetItems
                            .filter(it => it.level === 'rw' && it.kecamatan === editForm.kecamatan && it.desa === editForm.desa && it.target_suara_2029 !== null && it.target_suara_2029 !== undefined)
                            .map(it => String(it.rw || '').toLowerCase())
                        );
                        const availableRwsFromStats = rwRealisasiList
                          .filter(r => r.kecamatan === editForm.kecamatan && r.desa === editForm.desa && !savedRwsInDesa.has(String(r.rw).toLowerCase()))
                          .map(r => r.rw);

                        return (
                          <>
                            {availableRwsFromStats.length > 0 && (
                              <div>
                                <span className="text-[11px] text-gray-500 font-medium block mb-1">Pilih RW yang tercatat:</span>
                                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-1 border border-gray-100 rounded-lg bg-gray-50/50">
                                  {availableRwsFromStats.map(r => (
                                    <button
                                      key={r}
                                      type="button"
                                      onClick={() => setEditForm(prev => ({ ...prev, rw: r }))}
                                      className={`px-2 py-0.5 rounded text-[11px] font-bold border transition-colors ${
                                        editForm.rw === r ? 'bg-orange-600 text-white border-orange-600 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'
                                      }`}
                                    >
                                      {r}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            <Input
                              type="text"
                              placeholder="Ketik nama/nomor RW (contoh: RW 01)"
                              value={editForm.rw}
                              onChange={e => setEditForm({ ...editForm, rw: e.target.value })}
                              className="h-9 text-xs"
                            />
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <Input
                      type="text"
                      disabled
                      value={editForm.rw}
                      className="h-9 text-xs bg-gray-50 font-bold text-gray-800 cursor-not-allowed"
                    />
                  )}
                </div>
              )}

              {/* Baseline 2024 */}
              <div>
                <label className="block text-gray-700 font-bold mb-1">
                  Baseline Suara 2024 (Historis Terverifikasi)
                </label>
                {editForm.level === 'desa' ? (
                  <div className="h-9 px-3 flex items-center bg-gray-50 border border-gray-200 rounded-md font-bold text-gray-600 text-xs">
                    {editForm.baseline_suara_2024 ? `${formatNumber(editForm.baseline_suara_2024)} suara (Read-only)` : '— (Belum ada data historis)'}
                  </div>
                ) : (
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    placeholder="Kosongkan jika belum ada data historis"
                    value={editForm.baseline_suara_2024}
                    onChange={e => setEditForm({ ...editForm, baseline_suara_2024: e.target.value })}
                    className="h-9 text-xs"
                  />
                )}
              </div>

              {/* Target Suara 2029 */}
              <div>
                <label className="block text-gray-700 font-bold mb-1">
                  Target Suara 2029 <span className="text-rose-500">*</span>
                </label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  placeholder={editForm.level === 'desa' ? "Masukkan target suara desa (bilangan bulat positif)" : "Masukkan target suara RW (non-negatif)"}
                  value={editForm.target_suara_2029}
                  onChange={e => setEditForm({ ...editForm, target_suara_2029: e.target.value })}
                  className="h-9 text-xs font-bold text-orange-600"
                />
              </div>

              {/* Kategori RW (Khusus Level RW) */}
              {editForm.level === 'rw' && (
                <div>
                  <label className="block text-gray-700 font-bold mb-1">
                    Kategori RW (Opsional)
                  </label>
                  <Input
                    type="text"
                    placeholder="Contoh: Ring 1, Basis Utama, Penguatan, dll"
                    value={editForm.kategori || ''}
                    onChange={e => setEditForm({ ...editForm, kategori: e.target.value })}
                    className="h-9 text-xs"
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Klasifikasi teritorial bebas internal RW</p>
                </div>
              )}

              {/* Catatan Analis */}
              <div>
                <label className="block text-gray-700 font-bold mb-1">Catatan / Keterangan (Opsional)</label>
                <Input
                  type="text"
                  placeholder="Contoh: Kesepakatan musyawarah tim atau pemetaan DPT"
                  value={editForm.catatan}
                  onChange={e => setEditForm({ ...editForm, catatan: e.target.value })}
                  className="h-9 text-xs"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button type="button" variant="outline" size="sm" onClick={requestCloseModal} disabled={saving}>
                Batal
              </Button>
              <Button type="button" size="sm" onClick={handleSaveEdit} disabled={saving} className="bg-orange-600 hover:bg-orange-700 font-bold text-white shadow-sm">
                {saving ? 'Menyimpan...' : 'Simpan Target'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DIALOG MODAL UPLOAD / IMPORT TARGET EXCEL (SUPER ADMIN ONLY) */}
      {uploadModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="font-extrabold text-base text-gray-900 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                Upload Target Suara Wilayah (Excel / CSV)
              </h3>
              <button
                type="button"
                onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                className="text-gray-400 hover:text-gray-600 font-bold p-1 rounded-lg hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-900 space-y-1">
                <p className="font-bold flex items-center gap-1.5 text-amber-800">
                  <Info className="w-4 h-4 text-amber-600" />
                  Mekanisme Replace Otomatis (UPSERT)
                </p>
                <p className="text-[11px] leading-relaxed text-amber-800/90">
                  Data target yang Anda unggah akan <b>langsung menimpa (replace)</b> angka target lama pada wilayah terkait (Dapil, Kecamatan, Desa, atau RW). Jika terdapat data target baru yang sebelumnya belum ada, sistem akan menambahkannya secara otomatis.
                </p>
              </div>

              <div>
                <label className="block text-gray-700 font-bold mb-1.5">
                  Pilih File Excel (.xlsx/.xls) atau .csv <span className="text-rose-500">*</span>
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setUploadFile(e.target.files[0] || null)}
                  className="block w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 border border-gray-200 rounded-lg p-1.5 cursor-pointer"
                />
              </div>

              {uploadFile && (
                <div className="p-2.5 rounded-lg bg-emerald-50/70 border border-emerald-200 text-emerald-900 flex items-center justify-between">
                  <span className="font-semibold truncate">{uploadFile.name}</span>
                  <span className="font-bold text-[10px] text-emerald-700">{(uploadFile.size / 1024).toFixed(1)} KB</span>
                </div>
              )}

              <div className="pt-2 border-t flex items-center justify-between">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                  className="text-xs font-bold border-gray-200 text-gray-700 hover:bg-gray-50 gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  Unduh Template
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => { setUploadModalOpen(false); setUploadFile(null); }}
                    className="text-xs font-bold text-gray-500"
                  >
                    Batal
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={!uploadFile || uploading}
                    onClick={handleUploadTarget}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs gap-1.5 shadow-sm"
                  >
                    {uploading ? "Mengunggah..." : "Mulai Upload & Sinkron"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Progress Suara (read from API) ============
const RANK_MEDAL = ['🥇', '🥈', '🥉'];
const rankTone = (i) => i === 0 ? 'bg-amber-50 border-amber-200' : i === 1 ? 'bg-gray-50 border-gray-200' : i === 2 ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-100';

const LeaderboardRow = ({ rank, label, sub, pct }) => (
  <div className={`flex items-center gap-3 p-3 rounded-xl border ${rankTone(rank)}`}>
    <span className="w-8 text-center font-extrabold text-sm flex-shrink-0">{RANK_MEDAL[rank] || `#${rank + 1}`}</span>
    <div className="flex-1 min-w-0">
      <div className="flex justify-between items-center gap-2">
        <span className="font-bold text-sm truncate">{label}</span>
        <span className="font-extrabold text-orange-600 text-sm flex-shrink-0">{pct}%</span>
      </div>
      <p className="text-[10px] text-gray-500 font-semibold">{sub}</p>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
        <div className="h-full bg-gradient-to-r from-orange-400 to-orange-500" style={{width: `${Math.min(100, pct)}%`}}></div>
      </div>
    </div>
  </div>
);

export const ProgressSuara = () => {
  const [items, setItems] = useState([]);
  const [dapilList, setDapilList] = useState([]);
  useEffect(() => { wilayahTargetApi.list().then(r => setItems(r.data)); }, []);
  useEffect(() => { dapilApi.list().then(r => setDapilList(r.data)).catch(() => {}); }, []);

  const areaData = items.map((r,i) => ({ month: r.kecamatan.slice(0,3), realisasi: r.realisasi, target: r.target }));

  // Papan progres per kecamatan (Ketua DPC) - hanya yang sudah punya target,
  // diurutkan dari % realisasi tertinggi.
  const kecRanked = [...items]
    .filter(k => k.target > 0)
    .map(k => ({ ...k, pct: Math.round((k.realisasi / k.target) * 100) }))
    .sort((a, b) => b.pct - a.pct);

  // Papan progres per Dapil (BPC) - jumlahkan realisasi/target semua kecamatan
  // dalam satu Dapil, lalu ranking berdasarkan % gabungannya.
  const dapilRanked = dapilList
    .map(d => {
      const kecInDapil = items.filter(k => d.kecamatan.includes(k.kecamatan));
      const realisasi = kecInDapil.reduce((sum, k) => sum + (k.realisasi || 0), 0);
      const target = kecInDapil.reduce((sum, k) => sum + (k.target || 0), 0);
      return { nama: d.nama, realisasi, target, kecCount: kecInDapil.length, pct: target > 0 ? Math.round((realisasi / target) * 100) : 0 };
    })
    .filter(d => d.target > 0)
    .sort((a, b) => b.pct - a.pct);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 card-shadow">
          <h3 className="text-lg font-extrabold mb-1 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Papan Progres Dapil</h3>
          <p className="text-xs text-gray-500 font-medium mb-4">Ranking gabungan realisasi/target semua kecamatan dalam tiap Dapil</p>
          <div className="space-y-2">
            {dapilRanked.length === 0 && <p className="text-xs text-gray-400 font-medium text-center py-6">Belum ada target yang diisi</p>}
            {dapilRanked.map((d, i) => (
              <LeaderboardRow key={d.nama} rank={i} label={d.nama} sub={`${formatNumber(d.realisasi)} / ${formatNumber(d.target)} suara · ${d.kecCount} kecamatan`} pct={d.pct} />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 card-shadow">
          <h3 className="text-lg font-extrabold mb-1 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Papan Progres Kecamatan (DPC)</h3>
          <p className="text-xs text-gray-500 font-medium mb-4">Ranking % realisasi terhadap target per kecamatan</p>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {kecRanked.length === 0 && <p className="text-xs text-gray-400 font-medium text-center py-6">Belum ada target yang diisi</p>}
            {kecRanked.map((k, i) => (
              <LeaderboardRow key={k.id} rank={i} label={k.kecamatan} sub={`${formatNumber(k.realisasi)} / ${formatNumber(k.target)} suara`} pct={k.pct} />
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow">
        <h3 className="text-lg font-extrabold mb-5">Perbandingan Realisasi per Kecamatan</h3>
        <ResponsiveContainer width="100%" height={340}>
          <AreaChart data={areaData}>
            <defs>
              <linearGradient id="gradOrange" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F97316" stopOpacity={0.5}/>
                <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="month" stroke="#9CA3AF" fontSize={11} />
            <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={v => `${v/1000}K`} />
            <Tooltip formatter={v => formatNumber(v)} />
            <Area type="monotone" dataKey="realisasi" stroke="#F97316" fillOpacity={1} fill="url(#gradOrange)" strokeWidth={3} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow">
        <h3 className="text-lg font-extrabold mb-5">Progress Kecamatan</h3>
        <div className="space-y-4">
          {items.map(k => {
            const pct = k.target ? Math.round((k.realisasi/k.target)*100) : 0;
            return (
              <div key={k.id}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-bold">{k.kecamatan}</span>
                  <span className="font-extrabold text-orange-600">{formatNumber(k.realisasi)} / {formatNumber(k.target)} · {pct}%</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-orange-400 to-orange-500" style={{width: `${Math.min(100,pct)}%`}}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const BaselineSuara = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { wilayahTargetApi.list().then(r => setItems(r.data)); }, []);
  return (
    <div className="bg-white rounded-2xl p-6 card-shadow">
      <h3 className="text-lg font-extrabold mb-5">Baseline Suara vs Target per Kecamatan</h3>
      <ResponsiveContainer width="100%" height={480}>
        <BarChart data={items} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis type="number" stroke="#9CA3AF" fontSize={11} tickFormatter={v => `${v/1000}K`} />
          <YAxis dataKey="kecamatan" type="category" stroke="#9CA3AF" fontSize={11} width={110} />
          <Tooltip formatter={v => formatNumber(v)} />
          <Legend />
          <Bar dataKey="baseline" name="Baseline" fill="#FDBA74" radius={[0,4,4,0]} />
          <Bar dataKey="target" name="Target" fill="#F97316" radius={[0,4,4,0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const QuickCount = () => {
  const { user } = React.useContext(AuthContext) || {};
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([quickCountApi.list(), quickCountApi.summary()])
      .then(([a, b]) => { setItems(a.data); setSummary(b.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const isSaksi = user?.role === 'saksi';
  const isAdmin = ['super_admin','admin_pusat','admin_input'].includes(user?.role);
  const canDelete = ['super_admin','admin_pusat'].includes(user?.role);

  const FIELDS = [
    { type: 'section', label: 'Info TPS' },
    { name: 'tps', label: 'No. TPS', required: true, placeholder: 'TPS 01', ...(isSaksi ? {} : {}) },
    { name: 'kecamatan', label: 'Kecamatan', required: true, type: 'select', options: ALL_KEC.map(k => ({value:k.name,label:k.name})) },
    { name: 'desa', label: 'Desa / Kelurahan' },
    { name: 'dpt', label: 'Jumlah DPT (Pemilih)', placeholder: '0' },
    { type: 'section', label: 'Perolehan Suara Sah' },
    { name: 'paslon_1', label: 'Suara Paslon 1', placeholder: '0' },
    { name: 'paslon_2', label: 'Suara Paslon 2 (Kami)', placeholder: '0' },
    { name: 'paslon_3', label: 'Suara Paslon 3', placeholder: '0' },
    { name: 'suara_tidak_sah', label: 'Suara Tidak Sah', placeholder: '0' },
    { name: 'catatan', label: 'Catatan (opsional)', full: true, placeholder: 'Kejadian penting di TPS...' },
  ];

  const handleSubmit = async (data) => {
    try {
      const payload = {
        tps: data.tps, kecamatan: data.kecamatan, desa: data.desa || '',
        paslon_1: Number(data.paslon_1) || 0, paslon_2: Number(data.paslon_2) || 0, paslon_3: Number(data.paslon_3) || 0,
        suara_tidak_sah: Number(data.suara_tidak_sah) || 0, dpt: Number(data.dpt) || 0, catatan: data.catatan || '',
      };
      if (editData) await quickCountApi.update(editData.id, payload);
      else await quickCountApi.create(payload);
      toast({ title: editData ? 'Hasil TPS diperbarui' : 'Hasil TPS terkirim' });
      load(); return true;
    } catch (e) {
      toast({ title: 'Gagal simpan', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return false;
    }
  };

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm flex-1">
          <p className="font-bold text-orange-900">Cara Kerja Quick Count</p>
          <p className="text-xs text-orange-800 font-medium mt-1">
            Setiap <b>Saksi TPS</b> input hasil suara di TPS-nya lewat tombol "Input Hasil TPS".
            Sistem akan otomatis merekap dan menampilkan hasil real-time.
            Data ter-update setiap 30 detik.
          </p>
        </div>
        {(isSaksi || isAdmin) && (
          <Button onClick={() => { setEditData(null); setDialogOpen(true); }} className="bg-orange-500 hover:bg-orange-600 gap-2 font-bold h-11">
            <Plus className="w-4 h-4" /> Input Hasil TPS
          </Button>
        )}
      </div>

      {/* Stats coverage */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { l: 'TPS Terlapor', v: summary?.total_tps_terlapor || 0, sub: `dari ${summary?.target_tps || 0} TPS target`, c: 'orange' },
          { l: 'Coverage TPS', v: `${summary?.coverage_persen || 0}%`, sub: 'Rekap masuk', c: 'emerald' },
          { l: 'Total Suara Masuk', v: formatNumber(summary?.total_suara || 0), sub: `dari DPT ${formatNumber(summary?.total_dpt || 0)}`, c: 'blue' },
          { l: 'Partisipasi', v: `${summary?.partisipasi_persen || 0}%`, sub: 'suara masuk / DPT', c: 'amber' },
        ].map(s => (
          <div key={s.l} className="bg-white rounded-2xl p-5 card-shadow card-hover">
            <div className={`w-11 h-11 rounded-xl bg-${s.c}-50 flex items-center justify-center mb-3`}>
              <Vote className={`w-5 h-5 text-${s.c}-600`} />
            </div>
            <p className="text-sm font-semibold text-gray-500">{s.l}</p>
            <h3 className="text-2xl font-extrabold text-gray-900 mt-1">{s.v}</h3>
            <p className="text-[10px] text-gray-400 font-semibold mt-1">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 card-shadow">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center"><Vote className="w-5 h-5 text-orange-600" /></div>
            <div>
              <h3 className="text-lg font-extrabold">Hasil Real-time</h3>
              <p className="text-xs text-gray-500 font-medium">Auto-refresh tiap 30 detik</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={summary?.paslon || []} dataKey="suara" nameKey="nama" innerRadius={65} outerRadius={100} paddingAngle={3}>
                {(summary?.paslon || []).map((e, i) => <Cell key={i} fill={e.warna} />)}
              </Pie>
              <Tooltip formatter={v => formatNumber(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 card-shadow">
          <h3 className="text-lg font-extrabold mb-5">Perolehan Suara Sah</h3>
          <div className="space-y-4">
            {(summary?.paslon || []).map(p => (
              <div key={p.nama}>
                <div className="flex justify-between mb-1">
                  <span className="font-extrabold">{p.nama}</span>
                  <span className="font-extrabold text-lg" style={{ color: p.warna }}>{p.persen}% <span className="text-xs text-gray-400 font-semibold">({formatNumber(p.suara)})</span></span>
                </div>
                <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${p.persen}%`, background: p.warna }}></div>
                </div>
              </div>
            ))}
          </div>
          {summary?.paslon && summary.paslon.length > 0 && summary.total_suara_sah > 0 && (
            <div className="mt-6 p-4 rounded-xl bg-orange-50 border border-orange-200">
              <p className="text-xs font-bold text-orange-700">STATUS: DATA MASUK {summary?.coverage_persen}%</p>
              <p className="text-sm font-semibold text-gray-700 mt-1">
                {summary.paslon.reduce((max, p) => p.persen > max.persen ? p : max, summary.paslon[0]).nama} memimpin dengan {summary.paslon.reduce((max, p) => p.persen > max.persen ? p : max, summary.paslon[0]).persen}%
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Daftar hasil TPS */}
      <div className="bg-white rounded-2xl p-6 card-shadow">
        <h3 className="text-lg font-extrabold mb-5">Daftar Hasil per TPS ({items.length})</h3>
        {loading ? <p className="text-center text-gray-400 py-8 font-semibold">Memuat...</p> :
         items.length === 0 ? <p className="text-center text-gray-400 py-8 font-semibold">Belum ada hasil TPS terkirim. Saksi bisa mulai input.</p> :
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-orange-50/50">
                <TableHead className="font-extrabold">TPS</TableHead>
                <TableHead className="font-extrabold">Kecamatan / Desa</TableHead>
                <TableHead className="font-extrabold text-right">Paslon 1</TableHead>
                <TableHead className="font-extrabold text-right bg-orange-100">Paslon 2 (Kami)</TableHead>
                <TableHead className="font-extrabold text-right">Paslon 3</TableHead>
                <TableHead className="font-extrabold text-right">Tidak Sah</TableHead>
                <TableHead className="font-extrabold">Waktu Input</TableHead>
                <TableHead className="font-extrabold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(it => (
                <TableRow key={it.id} className="hover:bg-orange-50/30">
                  <TableCell><Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100"><MapPinIcon className="w-3 h-3 mr-1" />{it.tps}</Badge></TableCell>
                  <TableCell className="font-medium text-xs">{it.kecamatan}<br/><span className="text-gray-400">{it.desa || '-'}</span></TableCell>
                  <TableCell className="text-right font-bold text-red-600">{formatNumber(it.paslon_1)}</TableCell>
                  <TableCell className="text-right font-extrabold text-orange-600 bg-orange-50/30">{formatNumber(it.paslon_2)}</TableCell>
                  <TableCell className="text-right font-bold text-blue-600">{formatNumber(it.paslon_3)}</TableCell>
                  <TableCell className="text-right font-medium text-gray-500">{formatNumber(it.suara_tidak_sah)}</TableCell>
                  <TableCell className="text-xs font-semibold text-gray-500">{new Date(it.submitted_at).toLocaleString('id-ID', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {(isAdmin || (isSaksi && it.submitted_by === user?.username)) && <button onClick={() => { setEditData(it); setDialogOpen(true); }} className="p-1.5 rounded-lg hover:bg-orange-50 text-gray-500 hover:text-orange-600"><Edit2 className="w-4 h-4" /></button>}
                      {canDelete && <button onClick={() => setConfirmDel(it)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>}
      </div>

      <EntityFormDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        title={editData ? `Edit Hasil ${editData.tps}` : 'Input Hasil TPS'}
        description="Masukkan perolehan suara sah tiap paslon. Data ter-update ke rekap secara real-time."
        fields={FIELDS} initialData={editData}
        onSubmit={handleSubmit}
      />
      <ConfirmDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Hapus Hasil TPS?" description={`Data ${confirmDel?.tps} akan dihapus.`}
        onConfirm={async () => { try { await quickCountApi.remove(confirmDel.id); toast({title:'Terhapus'}); load(); } catch (e) { toast({title:'Gagal', variant:'destructive'}); } }}
      />
    </div>
  );
};
